/**
 * Connector IDs that have a full implementation (Zod schema, setup form, route).
 * This is the single source of truth — keep it in sync with the `implemented: true`
 * entries in FIVETRAN_CONNECTORS below.
 */
export const IMPLEMENTED_CONNECTOR_IDS = [
  "posthog",
  "stripe",
  "salesforce",
  "attio",
  "hubspot",
  "google_sheets",
  "facebook_ads",
  "github",
  "shopify",
  "quickbooks",
  "amazon_selling_partner",
  "klaviyo",
  "triple_whale",
  "fulfil",
  "netsuite_suiteanalytics",
] as const;

export type ImplementedConnectorId = (typeof IMPLEMENTED_CONNECTOR_IDS)[number];

/**
 * Connectors surfaced in the picker's default ("Available now") grid. A curated
 * subset of IMPLEMENTED_CONNECTOR_IDS — the others remain fully connectable and
 * searchable, just not featured by default. Order here controls display order.
 */
export const FEATURED_CONNECTOR_IDS: readonly ImplementedConnectorId[] = [
  "posthog",
  "stripe",
  "salesforce",
  "attio",
  "hubspot",
  "google_sheets",
  "facebook_ads",
  "github",
  "shopify",
  "quickbooks",
];

/**
 * Connectors that authenticate after creation via the Fivetran Connect Card
 * (OAuth). Their setup tests are expected to be incomplete at create time, so
 * we skip strict setup-test gating for them. The remaining implemented
 * connectors (`posthog`, `stripe`) supply credentials in `config` at create
 * time, so their setup tests are meaningful immediately.
 */
export const CONNECT_CARD_AUTH_SERVICES = new Set<ImplementedConnectorId>([
  "github",
  "attio",
  "hubspot",
  "salesforce",
  "google_sheets",
  "facebook_ads",
  "shopify",
  "quickbooks",
  "amazon_selling_partner",
]);

/** Whether a service authenticates later via the Fivetran Connect Card (OAuth). */
export function isConnectCardAuthService(service: string): boolean {
  return CONNECT_CARD_AUTH_SERVICES.has(service as ImplementedConnectorId);
}

/**
 * All Fivetran connector types, retrieved from Fivetran's public connector
 * metadata endpoint (GET https://api.fivetran.com/public/connector-types).
 *
 * Each entry's `id` matches the connector's service identifier used throughout
 * the Fivetran REST API and docs (e.g. `google_ads`, `stripe`, `hubspot`).
 */
export interface FivetranConnector {
  /** Fivetran service identifier (slug) used by the Fivetran API and docs. */
  id: string;
  /** Human-readable display name. */
  name: string;
  /** Short description of the connector. */
  description: string;
  /** Logo identifier for the connector icon sprite. Null when no logo is available. */
  logo: string | null;
  /** Whether this connector has a working implementation with a setup form and route. */
  implemented: boolean;
}

export const FIVETRAN_CONNECTORS: FivetranConnector[] = [
  {
    id: "15five",
    name: "15Five",
    description: "15Five is an employee performance management platform.",
    logo: "15five",
    implemented: false,
  },
  {
    id: "360learning",
    name: "360Learning",
    description: "360Learning is a learning and development platform.",
    logo: "360learning",
    implemented: false,
  },
  {
    id: "6sense",
    name: "6sense",
    description:
      "6sense is a predictive intelligence platform for marketing and sales.",
    logo: null,
    implemented: false,
  },
  {
    id: "7shifts",
    name: "7shifts",
    description: "7shifts is a team management software for restaurants.",
    logo: "7shifts",
    implemented: false,
  },
  {
    id: "absorb_lms",
    name: "Absorb LMS",
    description: "Absorb LMS is a cloud-based learning management system.",
    logo: "absorb-lms",
    implemented: false,
  },
  {
    id: "acculynx",
    name: "AccuLynx",
    description: "AccuLynx is a business management software.",
    logo: null,
    implemented: false,
  },
  {
    id: "activecampaign",
    name: "ActiveCampaign",
    description: "ActiveCampaign is a customer experience automation platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "acumatica",
    name: "Acumatica",
    description: "Acumatica is a cloud-based ERP software.",
    logo: "acumatica",
    implemented: false,
  },
  {
    id: "ada",
    name: "Ada",
    description: "Ada is an automated brand interaction platform.",
    logo: "ada",
    implemented: false,
  },
  {
    id: "adform",
    name: "Adform",
    description: "Adform is a digital advertising platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "adjust",
    name: "Adjust",
    description:
      "Adjust is a mobile measurement and fraud prevention platform.",
    logo: "adjust",
    implemented: false,
  },
  {
    id: "adobe_analytics",
    name: "Adobe Analytics",
    description:
      "Adobe Analytics provides marketing analytics and reporting tools.",
    logo: "adobe-analytics",
    implemented: false,
  },
  {
    id: "adobe_analytics_data_feed",
    name: "Adobe Analytics Data Feed",
    description: "Adobe Analytics Data Feeds provide raw events data",
    logo: "adobe-analytics",
    implemented: false,
  },
  {
    id: "adobe_commerce",
    name: "Adobe Commerce",
    description:
      "Adobe Commerce is a digital commerce solution for merchants and brands.",
    logo: "adobe-commerce",
    implemented: false,
  },
  {
    id: "adobe_experience_platform",
    name: "Adobe Experience Platform",
    description:
      "Adobe Experience Platform is a cloud-based customer experience management platform.",
    logo: "adobe-experience-platform",
    implemented: false,
  },
  {
    id: "adobe_workfront",
    name: "Adobe Workfront",
    description: "Adobe Workfront is a cloud-based work management platform.",
    logo: "adobe-workfront",
    implemented: false,
  },
  {
    id: "adp_workforce_now",
    name: "ADP Workforce Now",
    description:
      "ADP Workforce Now is a all-in-one platform for payroll and HR software, providing expert support and analytics for data-driven insights",
    logo: "adp-workforce-now",
    implemented: false,
  },
  {
    id: "adroll",
    name: "AdRoll",
    description: "AdRoll is prospecting and retargeting platform",
    logo: "adroll",
    implemented: false,
  },
  {
    id: "adyen",
    name: "Adyen",
    description:
      "Adyen is a payment, revenue, and financial management platform.",
    logo: "adyen",
    implemented: false,
  },
  {
    id: "affinity",
    name: "Affinity",
    description: "Affinity is a CRM platform.",
    logo: "affinity",
    implemented: false,
  },
  {
    id: "afterpay",
    name: "Afterpay",
    description: "Afterpay is a digital payment platform.",
    logo: "afterpay",
    implemented: false,
  },
  {
    id: "aha",
    name: "Aha!",
    description: "Aha! is a cloud-based product development software.",
    logo: "aha",
    implemented: false,
  },
  {
    id: "aircall",
    name: "Aircall",
    description: "Aircall is a cloud-based call center and phone system.",
    logo: "aircall",
    implemented: false,
  },
  {
    id: "airtable",
    name: "Airtable",
    description:
      "Airtable is an online collaboration platform for creating and sharing relational databases.",
    logo: "airtable",
    implemented: false,
  },
  {
    id: "akamai",
    name: "Akamai",
    description: "Akamai is a content delivery network.",
    logo: "akamai",
    implemented: false,
  },
  {
    id: "akeneo",
    name: "Akeneo",
    description: "Akeneo is a Product Experience Management solution.",
    logo: "akeneo",
    implemented: false,
  },
  {
    id: "alation",
    name: "Alation",
    description: "Alation is a data intelligence platform.",
    logo: "alation",
    implemented: false,
  },
  {
    id: "alchemer",
    name: "Alchemer",
    description: "Alchemer is a feedback and data collection platform.",
    logo: "alchemer",
    implemented: false,
  },
  {
    id: "algolia",
    name: "Algolia",
    description:
      "Algolia is a search and discovery platform for websites and applications.",
    logo: "algolia",
    implemented: false,
  },
  {
    id: "alida",
    name: "Alida",
    description: "Alida is a customer experience management platform.",
    logo: "alida",
    implemented: false,
  },
  {
    id: "amazon_ads",
    name: "Amazon Ads",
    description:
      "Amazon Ads is an advertising service that uses the pay-per-click model.",
    logo: "amazon",
    implemented: false,
  },
  {
    id: "amazon_attribution",
    name: "Amazon Attribution",
    description:
      "Amazon Attribution is an advertising and analytics measurement solution.",
    logo: "amazon",
    implemented: false,
  },
  {
    id: "amazon_dsp",
    name: "Amazon DSP",
    description: "Amazon DSP is a demand-side platform for advertisers.",
    logo: "amazon",
    implemented: false,
  },
  {
    id: "amazon_selling_partner",
    name: "Amazon Selling Partner",
    description:
      "Amazon Selling Partner is Amazon's sales activity monitoring portal.",
    logo: "amazon",
    implemented: true,
  },
  {
    id: "amplitude",
    name: "Amplitude",
    description:
      "Amplitude is the comprehensive product analytics software for web and mobile",
    logo: "amplitude",
    implemented: false,
  },
  {
    id: "amqp",
    name: "AMQP",
    description:
      "AMQP Connector enables secure, reliable, and standards-based messaging that support the Advanced Message Queuing Protocol (AMQP 1.0) — including Solace Solace, RabbitMQ, and others.",
    logo: "amqp",
    implemented: false,
  },
  {
    id: "anaplan",
    name: "Anaplan",
    description:
      "Anaplan is a cloud-based platform for business planning and modeling.",
    logo: "anaplan",
    implemented: false,
  },
  {
    id: "anthropic_claude",
    name: "Claude Platform",
    description:
      "Claude Platform is Anthropic’s API platform for building with and managing Claude AI models.",
    logo: "claude",
    implemented: false,
  },
  {
    id: "anvyl",
    name: "Anvyl",
    description: "Anvyl is a supply chain management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "apache_kafka",
    name: "Apache Kafka",
    description:
      "Apache Kafka is an open-source distributed streaming platform for building real-time data pipelines and stream processing applications.",
    logo: "apache-kafka",
    implemented: false,
  },
  {
    id: "apollo",
    name: "Apollo",
    description: "Apollo is an AI sales platform.",
    logo: "apollo",
    implemented: false,
  },
  {
    id: "appcues",
    name: "Appcues",
    description: "Appcues is an in-app user engagement platform.",
    logo: "appcues",
    implemented: false,
  },
  {
    id: "appfigures",
    name: "Appfigures",
    description:
      "Appfigures is a reporting platform for mobile application developers.",
    logo: "appfigures",
    implemented: false,
  },
  {
    id: "apple_search_ads",
    name: "Apple Search Ads",
    description:
      "Apple Search Ads helps people discover your app on the App Store for iPhone and iPad.",
    logo: "apple-search-ads",
    implemented: false,
  },
  {
    id: "applovin",
    name: "AppLovin",
    description:
      "AppLovin is a mobile technology company that provides a leading marketing platform.",
    logo: "applovin",
    implemented: false,
  },
  {
    id: "appsflyer",
    name: "AppsFlyer",
    description:
      "AppsFlyer is a mobile app tracking and attribution analytics platform.",
    logo: "appsflyer",
    implemented: false,
  },
  {
    id: "asana",
    name: "Asana",
    description:
      "Asana is a project management tool that helps teams track their tasks and projects.",
    logo: "asana",
    implemented: false,
  },
  {
    id: "ascend_by_partnerize",
    name: "Ascend by Partnerize",
    description:
      "Ascend by Partnerize is an integrated affiliate marketing platform.",
    logo: "ascend-by-partnerize",
    implemented: false,
  },
  {
    id: "ashby",
    name: "Ashby",
    description: "Ashby is a recruiting platform.",
    logo: "ashby",
    implemented: false,
  },
  {
    id: "assembled",
    name: "Assembled",
    description:
      "Assembled is a workforce management platform for customer service.",
    logo: "assembled",
    implemented: false,
  },
  {
    id: "asset_panda",
    name: "Asset Panda",
    description:
      "Asset Panda is a cloud-based asset tracking and management platform.",
    logo: "asset-panda",
    implemented: false,
  },
  {
    id: "atlassian_jira_align",
    name: "Jira Align",
    description:
      "Jira Align is a project management tool to coordinate software development activities across multiple teams.",
    logo: "jira-align",
    implemented: false,
  },
  {
    id: "atlassian_ops_genie",
    name: "Opsgenie",
    description:
      "Opsgenie is an incident management platform for development and operations teams.",
    logo: "opsgenie",
    implemented: false,
  },
  {
    id: "attentive",
    name: "Attentive",
    description: "Attentive is an SMS marketing platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "attio",
    name: "Attio",
    description: "Attio is a customer relationship management (CRM) platform.",
    logo: "attio",
    implemented: true,
  },
  {
    id: "aumni",
    name: "Aumni",
    description: "Aumni is an investment analytics platform.",
    logo: "aumni",
    implemented: false,
  },
  {
    id: "aura_from_unity",
    name: "Aura from Unity",
    description: "Aura from Unity is a mobile advertising platform.",
    logo: "aura-from-unity",
    implemented: false,
  },
  {
    id: "aurora",
    name: "Amazon Aurora MySQL",
    description: "Amazon Aurora MySQL is a relational database.",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "aurora_postgres",
    name: "Amazon Aurora PostgreSQL",
    description:
      "Amazon Aurora PostgreSQL is a hosted version of the popular Postgres database on the Amazon Aurora platform",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "auth0",
    name: "Auth0",
    description:
      "Auth0 is a cloud-based authentication service for applications.",
    logo: "auth0",
    implemented: false,
  },
  {
    id: "autodesk_bim_360",
    name: "Autodesk BIM 360 & Construction Cloud",
    description:
      "Autodesk BIM 360 & Construction Cloud is a cloud-based construction management platform.",
    logo: "autodesk-bim-360",
    implemented: false,
  },
  {
    id: "avantlink",
    name: "AvantLink",
    description: "AvantLink is a marketing network for affiliates.",
    logo: "avantlink",
    implemented: false,
  },
  {
    id: "aveva_pi",
    name: "AVEVA PI",
    description:
      "AVEVA PI (AVEVA PI System) is a data management software system used for real-time data collection, storage, and analysis.",
    logo: "aveva-pi",
    implemented: false,
  },
  {
    id: "awin",
    name: "Awin",
    description: "Awin is an affiliate marketing platform.",
    logo: "awin",
    implemented: false,
  },
  {
    id: "aws_cloudtrail",
    name: "AWS CloudTrail",
    description:
      "AWS CloudTrail is a service that enables governance, compliance, operational auditing, and risk auditing of your AWS account. With AWS CloudTrail, you can log, continuously monitor, and retain account activity related to actions across your AWS infrastructure.",
    logo: "aws-cloudtrail",
    implemented: false,
  },
  {
    id: "aws_cost_report",
    name: "AWS Cost and Usage Reports",
    description:
      "AWS Cost and Usage Reports is a tool to track and extract your cost and usage metrics.",
    logo: "aws",
    implemented: false,
  },
  {
    id: "aws_inventory",
    name: "AWS Inventory",
    description:
      "AWS Inventory is a comprehensive service that provides you with an Amazon Web Services (AWS) resource inventory, configuration history, and configuration change notifications. With AWS Inventory, you can fetch and view the details of the services of your AWS account.",
    logo: "aws",
    implemented: false,
  },
  {
    id: "aws_lambda",
    name: "AWS Lambda",
    description:
      "AWS Lambda is a serverless computing platform that runs code in response to events and automatically manages the compute resources required by that code.",
    logo: "aws-cloudtrail",
    implemented: false,
  },
  {
    id: "aws_msk",
    name: "AWS MSK",
    description:
      "AWS Managed Streaming for Kafka is a managed distributed streaming platform.",
    logo: "aws",
    implemented: false,
  },
  {
    id: "azure_blob_storage",
    name: "Azure Blob Storage",
    description:
      "Fivetran supports syncing files from Azure Blob Storage and Azure Data Lake Storage Gen2 (ADLS Gen2) to your destination.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_cosmos_for_mongo",
    name: "Azure Cosmos DB for MongoDB",
    description:
      "Azure Cosmos DB is a fully managed NoSQL and relational database for modern app development. Azure Cosmos DB for MongoDB makes it easy to use Azure Cosmos DB as if it were a MongoDB database It is a document-oriented database that uses JSON documents.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_devops",
    name: "Azure DevOps",
    description:
      "Azure DevOps supports a collaborative culture and set of processes that bring together developers,  project managers, and contributors to develop software.  It allows organizations to create and improve products at a faster pace than they can with traditional software development approaches. Boards provides interactive and customizable tools to manage software projects.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_event_hub",
    name: "Azure Event Hubs",
    description:
      "Azure Event Hubs is a managed distributed streaming platform.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_function",
    name: "Azure Functions",
    description:
      "Azure Functions is a serverless computing platform that runs code in response to events and automatically manages the compute resources required by that code.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_postgres",
    name: "Azure Database for PostgreSQL",
    description:
      "Azure Database for PostgreSQL is a managed relational cloud database service",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "azure_service_bus",
    name: "Azure Service Bus",
    description: "Azure Service Bus is a cloud messaging service.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_sql_db",
    name: "Azure SQL Database",
    description:
      "Azure SQL Database is a managed relational cloud database service.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "azure_sql_managed_db",
    name: "Azure SQL Managed Instance",
    description:
      "Azure SQL Managed Instance is a managed relational cloud database service.",
    logo: "azure",
    implemented: false,
  },
  {
    id: "backbone_plm",
    name: "Backbone PLM",
    description: "Backbone PLM is a product development platform.",
    logo: "backbone-plm",
    implemented: false,
  },
  {
    id: "ballotready",
    name: "BallotReady",
    description: "BallotReady is an online voter guide for elections.",
    logo: "ballotready",
    implemented: false,
  },
  {
    id: "bamboohr",
    name: "Bamboo HR",
    description:
      "BambooHR is a human resources management software as a service.",
    logo: "bamboohr",
    implemented: false,
  },
  {
    id: "bazaarvoice",
    name: "Bazaarvoice",
    description:
      "Bazaarvoice is a platform to collect and manage customer reviews and ratings.",
    logo: "bazaarvoice",
    implemented: false,
  },
  {
    id: "betterworks",
    name: "Betterworks",
    description: "Betterworks is a performance management solution.",
    logo: null,
    implemented: false,
  },
  {
    id: "big_commerce",
    name: "BigCommerce",
    description: "BigCommerce is an e-commerce platform for retailers.",
    logo: "",
    implemented: false,
  },
  {
    id: "bigin_by_zoho_crm",
    name: "Bigin by Zoho CRM",
    description:
      "Bigin by Zoho CRM is a pipeline-centric CRM for small businesses.",
    logo: "bigin-by-zoho-crm",
    implemented: false,
  },
  {
    id: "bigmarker",
    name: "BigMarker",
    description:
      "BigMarker is a complete online event hosting and marketing solution.",
    logo: "bigmarker",
    implemented: false,
  },
  {
    id: "bigquery_db",
    name: "BigQuery",
    description: "Cloud Database Service (https://cloud.google.com/bigquery)",
    logo: "bigquery",
    implemented: false,
  },
  {
    id: "billing_platform",
    name: "BillingPlatform",
    description:
      "BillingPlatform automates the process of generating and managing bills.",
    logo: "billing-platform",
    implemented: false,
  },
  {
    id: "bing_webmaster_tools",
    name: "Bing Webmaster Tools",
    description:
      "Bing Webmaster Tools helps improve site visibility on Bing search.",
    logo: "bing-webmaster-tools",
    implemented: false,
  },
  {
    id: "bingads",
    name: "Microsoft Advertising",
    description:
      "Microsoft Advertising (formerly known as Bing Ads) is a service that provides pay-per-click advertising for both the Bing and Yahoo search engines. To get an overview of Microsoft Advertising, see its entity hierarchy and limits documentation.",
    logo: "microsoft-advertising",
    implemented: false,
  },
  {
    id: "birdeye",
    name: "Birdeye",
    description: "Birdeye is an experience marketing platform.",
    logo: "birdeye",
    implemented: false,
  },
  {
    id: "bitly",
    name: "Bitly",
    description: "Bitly is a link management platform.",
    logo: "bitly",
    implemented: false,
  },
  {
    id: "bizzabo_app",
    name: "Bizzabo",
    description: "Bizzabo is an event software for B2B conferences.",
    logo: "bizzabo",
    implemented: false,
  },
  {
    id: "boostr",
    name: "Boostr",
    description: "Boostr is an advertising management platform.",
    logo: "boostr",
    implemented: false,
  },
  {
    id: "box",
    name: "Box",
    description:
      "Box is a cloud-based file storage service. Fivetran supports syncing files from your Box account to your destination. You can sync multiple files as unique tables to your destination.",
    logo: "box",
    implemented: false,
  },
  {
    id: "braintree",
    name: "Braintree",
    description:
      "Braintree helps businesses of all sizes, from small to large enterprise, accept, process, and split payments to help maximize business opportunities and revenue growth. Companies around the world benefit from the technology and support of Braintree coupled with the scale, backing, and confidence of partnering with a PayPal service.",
    logo: "braintree",
    implemented: false,
  },
  {
    id: "braintree_sandbox",
    name: "Braintree Sandbox",
    description:
      "Braintree helps businesses of all sizes, from small to large enterprise, accept, process, and split payments to help maximize business opportunities and revenue growth. Companies around the world benefit from the technology and support of Braintree coupled with the scale, backing, and confidence of partnering with a PayPal service.",
    logo: "braintree",
    implemented: false,
  },
  {
    id: "branch",
    name: "Branch",
    description:
      "Branch is a mobile measurement and deep linking platform that unifies user measurement across different devices, platforms, and channels.",
    logo: "branch",
    implemented: false,
  },
  {
    id: "brave_ads",
    name: "Brave Ads",
    description: "Brave Ads is an advertising platform.",
    logo: "brave-ads",
    implemented: false,
  },
  {
    id: "braze",
    name: "Braze",
    description: "Braze is a platform for cross-channel marketing automation.",
    logo: "braze",
    implemented: false,
  },
  {
    id: "breathe_hr",
    name: "Breathe HR",
    description:
      "Breathe HR is a comprehensive human resources platform that helps organizations manage employee data, performance, and engagement.",
    logo: null,
    implemented: false,
  },
  {
    id: "brevo",
    name: "Brevo",
    description: "Brevo is a digital marketing platform for businesses.",
    logo: "brevo",
    implemented: false,
  },
  {
    id: "brex",
    name: "Brex",
    description: "Brex is a spend management platform.",
    logo: "brex",
    implemented: false,
  },
  {
    id: "brightcove",
    name: "Brightcove",
    description: "Brightcove is a video streaming platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "brightflag",
    name: "Brightflag",
    description:
      "Brightflag is a legal technology platform that streamlines e-billing, matter management, and legal spend.",
    logo: "brightflag",
    implemented: false,
  },
  {
    id: "brightpearl",
    name: "Brightpearl",
    description:
      "Brightpearl is the industry-leading operating system for retailers.",
    logo: null,
    implemented: false,
  },
  {
    id: "brivo",
    name: "Brivo",
    description:
      "Brivo is a cloud-based access control platform for commercial real estate.",
    logo: "brivo",
    implemented: false,
  },
  {
    id: "buildium",
    name: "Buildium",
    description: "Buildium is a property management software.",
    logo: "buildium",
    implemented: false,
  },
  {
    id: "buildkite",
    name: "Buildkite",
    description:
      "Connect your Buildkite data to your destination using Fivetran. Learn about configuration requirements, setup, and ERDs with our technical documentation.",
    logo: "buildkite",
    implemented: false,
  },
  {
    id: "business_central",
    name: "Microsoft Dynamics 365 Business Central",
    description:
      "Microsoft Dynamics 365 Business Central is an enterprise resource planning system",
    logo: "business-central",
    implemented: false,
  },
  {
    id: "buzzsprout",
    name: "Buzzsprout",
    description: "Buzzsprout is a podcast hosting platform.",
    logo: "buzzsprout",
    implemented: false,
  },
  {
    id: "bynder",
    name: "Bynder",
    description: "Bynder is a digital asset management (DAM) platform.",
    logo: "bynder",
    implemented: false,
  },
  {
    id: "cabcharge",
    name: "Cabcharge",
    description: "Cabcharge is a travel management platform.",
    logo: "cabcharge",
    implemented: false,
  },
  {
    id: "calabrio",
    name: "Calabrio",
    description:
      "Calabrio is a workforce optimization and analytics solutions platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "calendly",
    name: "Calendly",
    description:
      "Calendly is an app for scheduling appointments, meetings, and events.",
    logo: "calendly",
    implemented: false,
  },
  {
    id: "callrail",
    name: "CallRail",
    description:
      "CallRail is a call tracking and marketing analytics platform.",
    logo: "callrail",
    implemented: false,
  },
  {
    id: "campaignmonitor",
    name: "Campaign Monitor",
    description: "Campaign Monitor is an email marketing automation tool.",
    logo: null,
    implemented: false,
  },
  {
    id: "campfire",
    name: "Campfire",
    description:
      "Campfire is an ERP platform for general ledger and revenue accounting.",
    logo: null,
    implemented: false,
  },
  {
    id: "canny",
    name: "Canny",
    description: "Canny is a customer feedback management tool.",
    logo: "canny",
    implemented: false,
  },
  {
    id: "canvas_by_instructure",
    name: "Canvas by Instructure",
    description: "Canvas by Instructure is a learning management system (LMS).",
    logo: "canvas-by-instructure",
    implemented: false,
  },
  {
    id: "canvas_data_2_by_instructure",
    name: "Canvas Data 2 by Instructure",
    description:
      "Canvas Data 2 by Instructure is an open-source learning management system.",
    logo: "canvas-by-instructure",
    implemented: false,
  },
  {
    id: "care_quality_commission",
    name: "Care Quality Commission",
    description:
      "Care Quality Commission is is a health and social care regulator.",
    logo: null,
    implemented: false,
  },
  {
    id: "castor_edc",
    name: "Castor EDC",
    description:
      "Castor EDC is a cloud-based data management platform for medical research and clinical trials.",
    logo: "castor-edc",
    implemented: false,
  },
  {
    id: "centra",
    name: "Centra",
    description: "Centra is an e-commerce platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "ceridian_dayforce",
    name: "Ceridian Dayforce",
    description: "Ceridian Dayforce is a workforce management platform.",
    logo: "ceridian-dayforce",
    implemented: false,
  },
  {
    id: "chameleon",
    name: "Chameleon",
    description: "Chameleon is a platform for user onboarding.",
    logo: null,
    implemented: false,
  },
  {
    id: "chargebee_product_catalog_1",
    name: "Chargebee Product Catalog 1.0",
    description:
      "Chargebee is a subscription billing and revenue management platform.",
    logo: "chargebee-product-catalog-1",
    implemented: false,
  },
  {
    id: "chargebee_product_catalog_2",
    name: "Chargebee Product Catalog 2.0",
    description:
      "Chargebee Product Catalog 2.0 is a billing, subscription, and revenue management platform.",
    logo: "chargebee-product-catalog-1",
    implemented: false,
  },
  {
    id: "chargedesk",
    name: "ChargeDesk",
    description: "ChargeDesk is a payment management service.",
    logo: "chargedesk",
    implemented: false,
  },
  {
    id: "charthop",
    name: "ChartHop",
    description: "ChartHop is a payment management service.",
    logo: "charthop",
    implemented: false,
  },
  {
    id: "chartmogul",
    name: "ChartMogul",
    description: "ChartMogul is a reporting and analytics solution.",
    logo: null,
    implemented: false,
  },
  {
    id: "checkout",
    name: "Checkout.com",
    description: "Checkout.com is a payment solutions platform.",
    logo: "checkout.com",
    implemented: false,
  },
  {
    id: "checkr",
    name: "Checkr",
    description: "Checkr is a background check and screening platform.",
    logo: "checkr",
    implemented: false,
  },
  {
    id: "chorusai",
    name: "Chorus",
    description: "Chorus is an AI conversation platform.",
    logo: "chorus",
    implemented: false,
  },
  {
    id: "churnkey",
    name: "Churnkey",
    description:
      "Churnkey is a subscription-based service that assists businesses in reducing their churn rate.",
    logo: "churnkey",
    implemented: false,
  },
  {
    id: "churnzero",
    name: "ChurnZero",
    description:
      "ChurnZero is a customer success platform for subscription-based businesses.",
    logo: "churnzero",
    implemented: false,
  },
  {
    id: "cimis",
    name: "Cimis",
    description:
      "Cimis (California Irrigation Management Information System) is a climate data repository.",
    logo: "cimis",
    implemented: false,
  },
  {
    id: "cin7",
    name: "Cin7",
    description:
      "Cin7 is a fully integrated, cloud-based inventory management software that streamlines stock management across multiple channels.",
    logo: "cin7",
    implemented: false,
  },
  {
    id: "cin7core",
    name: "Cin7 Core",
    description: "Cin7 Core is a cloud-based ERP solution.",
    logo: "cin7",
    implemented: false,
  },
  {
    id: "circleci",
    name: "CircleCI",
    description:
      "CircleCI is an integration and delivery platform that can be used to implement DevOps practices.",
    logo: "circleci",
    implemented: false,
  },
  {
    id: "cj_commission_detail",
    name: "CJ Commission Detail",
    description: "CJ Commission Detail is an affiliate marketing network.",
    logo: "cj-commission-detail",
    implemented: false,
  },
  {
    id: "clari",
    name: "Clari",
    description: "Clari is a revenue management platform.",
    logo: "clari",
    implemented: false,
  },
  {
    id: "clazar",
    name: "Clazar",
    description: "Clazar is a cloud sales acceleration platform.",
    logo: "clazar",
    implemented: false,
  },
  {
    id: "clickup",
    name: "ClickUp",
    description:
      "ClickUp is a cloud-based collaboration and project management tool.",
    logo: "clickup",
    implemented: false,
  },
  {
    id: "clockify",
    name: "Clockify",
    description:
      "Clockify is a time tracker and timesheet app that lets you track work hours across projects.",
    logo: "clockify",
    implemented: false,
  },
  {
    id: "clockodo",
    name: "Clockodo",
    description: "Clockodo is an online time tracking software.",
    logo: null,
    implemented: false,
  },
  {
    id: "close",
    name: "Close",
    description:
      "Close is a sales platform built to help small businesses grow faster.",
    logo: "close",
    implemented: false,
  },
  {
    id: "cloudbeds",
    name: "Cloudbeds",
    description:
      "Cloudbeds is a property management system for hospitality businesses.",
    logo: "cloudbeds",
    implemented: false,
  },
  {
    id: "cloudflare_analytics",
    name: "Cloudflare Analytics",
    description:
      "Cloudflare Analytics is a traffic and performance analysis tool.",
    logo: "cloudflare-analytics",
    implemented: false,
  },
  {
    id: "cloudfront",
    name: "Amazon CloudFront",
    description:
      "Amazon CloudFront is a content delivery network (CDN) offered by Amazon Web Services.",
    logo: "amazon-cloudfront",
    implemented: false,
  },
  {
    id: "cloudtalk",
    name: "CloudTalk",
    description:
      "CloudTalk is a cloud-based call center and communication software.",
    logo: "cloudtalk",
    implemented: false,
  },
  {
    id: "clubspeed",
    name: "Clubspeed",
    description: "Clubspeed is a venue management software.",
    logo: "clubspeed",
    implemented: false,
  },
  {
    id: "coalesce",
    name: "Coalesce",
    description: "Coalesce is a data transformation platform.",
    logo: "coalesce",
    implemented: false,
  },
  {
    id: "coassemble",
    name: "Coassemble",
    description: "Coassemble is an online training platform.",
    logo: "coassemble",
    implemented: false,
  },
  {
    id: "cockroachdb",
    name: "CockroachDB",
    description:
      "CockroachDB is a horizontally-scaling, distributed SQL database.",
    logo: "cockroachdb",
    implemented: false,
  },
  {
    id: "coda",
    name: "Coda",
    description:
      "Coda is a collaborative workspace for documents, tables, and workflows.",
    logo: null,
    implemented: false,
  },
  {
    id: "codefresh",
    name: "Codefresh",
    description: "Codefresh is a CI/CD platform for cloud-native applications.",
    logo: "codefresh",
    implemented: false,
  },
  {
    id: "collibra",
    name: "Collibra",
    description: "Collibra is a data intelligence cloud platform.",
    logo: "collibra",
    implemented: false,
  },
  {
    id: "column",
    name: "Column",
    description:
      "Column is a nationally charted infrastructure bank for developers.",
    logo: "column",
    implemented: false,
  },
  {
    id: "commercetools",
    name: "commercetools",
    description:
      "commercetools is a cloud-based commerce solution for global businesses.",
    logo: "commercetools",
    implemented: false,
  },
  {
    id: "compliance_checkpoint",
    name: "Compliance Checkpoint",
    description:
      "Compliance Checkpoints is a tool for maintaining adherence to regulatory and internal standards, managing risks, and ensuring ongoing operational integrity.",
    logo: null,
    implemented: false,
  },
  {
    id: "concord",
    name: "Concord",
    description: "Concord is a contract management platform.",
    logo: "concord",
    implemented: false,
  },
  {
    id: "concur",
    name: "SAP Concur",
    description:
      "SAP Concur provides an integrated online business travel and expense management software solution that automates your travel expenses.",
    logo: "sap-concur",
    implemented: false,
  },
  {
    id: "confluence",
    name: "Confluence",
    description: "Confluence is a content management platform.",
    logo: "confluence",
    implemented: false,
  },
  {
    id: "confluent_cloud",
    name: "Confluent Cloud",
    description: "Confluent Cloud is a managed distributed streaming platform.",
    logo: "confluent-cloud",
    implemented: false,
  },
  {
    id: "connector_sdk",
    name: "Connector SDK",
    description:
      "Fivetran Connector SDK runs your code at specified intervals to deliver  the returned response, and automatically manages the required compute resource",
    logo: null,
    implemented: false,
  },
  {
    id: "consensus_demo_automation_platform",
    name: "Consensus Demo Automation Platform",
    description:
      "Consensus Demo Automation Platform is a B2B sales enablement platform that automates product demos using interactive and personalized video experiences.",
    logo: "consensus-demo-automation-platform",
    implemented: false,
  },
  {
    id: "constant_contact",
    name: "Constant Contact",
    description: "Constant Contact is a digital and email marketing platform.",
    logo: "constant-contact",
    implemented: false,
  },
  {
    id: "contrast_security",
    name: "Contrast Security",
    description:
      "Contrast Security is an application and code security platform.",
    logo: "contrast-security",
    implemented: false,
  },
  {
    id: "convex",
    name: "Convex",
    description:
      "Convex is a fullstack TypeScript development platform. Replace your database, server functions and glue code.",
    logo: "convex",
    implemented: false,
  },
  {
    id: "copper",
    name: "Copper",
    description:
      "Copper is a CRM platform for businesses using Google Workspace.",
    logo: "copper",
    implemented: false,
  },
  {
    id: "cornerstone",
    name: "Cornerstone",
    description:
      "Cornerstone is a cloud-based learning and talent management software vendor.",
    logo: "cornerstone",
    implemented: false,
  },
  {
    id: "cosmos",
    name: "Azure Cosmos DB for NoSQL",
    description:
      "Azure Cosmos DB is a fully managed, serverless NoSQL database for high-performance applications.",
    logo: "cosmos",
    implemented: false,
  },
  {
    id: "coupa",
    name: "Coupa",
    description:
      "Coupa Software is a global technology platform for Business Spend Management",
    logo: "coupa",
    implemented: false,
  },
  {
    id: "credly",
    name: "Credly",
    description:
      "Credly empowers organizations to attract, engage, develop, and retain talent with enterprise-class tools",
    logo: "credly",
    implemented: false,
  },
  {
    id: "criteo",
    name: "Criteo",
    description:
      "Criteo helps internet retailers serve personalized advertisements through retargeting",
    logo: "criteo",
    implemented: false,
  },
  {
    id: "criteo_retail_media",
    name: "Criteo Retail Media",
    description: "Criteo Retail Media is a digital advertisemet solution.",
    logo: "criteo-retail-media",
    implemented: false,
  },
  {
    id: "crossbeam",
    name: "Crossbeam",
    description: "Crossbeam is a partner ecosystem platform.",
    logo: "crossbeam",
    implemented: false,
  },
  {
    id: "crowddev",
    name: "crowd.dev",
    description:
      "crowd.dev is a developer data platform for software companies.",
    logo: "crowddev",
    implemented: false,
  },
  {
    id: "crunchbase",
    name: "Crunchbase",
    description:
      "Crunchbase is a business intelligence platform for tracking company data, funding, and investors.",
    logo: "crunchbase",
    implemented: false,
  },
  {
    id: "cube_software",
    name: "Cube",
    description: "Cube is a financial planning platform.",
    logo: "cube-software",
    implemented: false,
  },
  {
    id: "culture_amp",
    name: "Culture Amp",
    description: "Culture Amp is an employee experience platform.",
    logo: "culture-amp",
    implemented: false,
  },
  {
    id: "customerio",
    name: "Customer.io",
    description: "Customer.io is an automated messaging platform.",
    logo: "customer.io",
    implemented: false,
  },
  {
    id: "cvent",
    name: "Cvent",
    description:
      "Cvent provides software solutions for event planning and marketing.",
    logo: null,
    implemented: false,
  },
  {
    id: "d2l_brightspace",
    name: "D2L Brightspace",
    description: "D2L Brightspace is a cloud-based learning management system.",
    logo: "d2l-brightspace",
    implemented: false,
  },
  {
    id: "databricks_db",
    name: "Databricks",
    description: "Databricks database connector",
    logo: "databricks",
    implemented: false,
  },
  {
    id: "datadog",
    name: "Datadog",
    description:
      "Datadog is a monitoring and analytics platform for cloud applications.",
    logo: "datadog",
    implemented: false,
  },
  {
    id: "datastreamer_source",
    name: "Datastreamer",
    description:
      "Datastreamer enables real-time threat detection, AI workflows, and complex data ingestion.",
    logo: "datastreamer#datastreamerconnector",
    implemented: false,
  },
  {
    id: "db2i_hva",
    name: "High Volume Agent Db2 for i",
    description:
      "Db2 for i is a relational database management system integrated with IBM i. Fivetran replicates data from your Db2 for i source database and loads it into your destination using High-Volume Agent connector.",
    logo: "hva_db2i",
    implemented: false,
  },
  {
    id: "db2luw",
    name: "Db2 for LUW",
    description:
      "Db2 for Linux, UNIX, and Windows (LUW) is a relational database created by IBM.",
    logo: "db2luw",
    implemented: false,
  },
  {
    id: "db2z",
    name: "Db2 for z/OS",
    description: "Db2 for z/OS is a relational database created by IBM.",
    logo: "db2-for-z-os-requirements",
    implemented: false,
  },
  {
    id: "dbt_cloud",
    name: "dbt Cloud",
    description:
      "dbt Cloud is a cloud-based platform for managing dbt data transformation projects.",
    logo: "dbt-cloud",
    implemented: false,
  },
  {
    id: "dcl_logistics",
    name: "DCL Logistics",
    description:
      "DCL Logistics is a logistics and supply chain management company.",
    logo: "dcl-logistics",
    implemented: false,
  },
  {
    id: "deel",
    name: "Deel",
    description: "Deel is the all-in-one HR platform for global teams.",
    logo: null,
    implemented: false,
  },
  {
    id: "delighted",
    name: "Delighted",
    description:
      "Delighted is an online solution that enables businesses to gather and  measure actionable feedback from customers, employees, partners, and vendors.",
    logo: "delighted",
    implemented: false,
  },
  {
    id: "deposco",
    name: "Deposco",
    description: "Deposco is a supply chain management platform.",
    logo: "deposco",
    implemented: false,
  },
  {
    id: "deputy",
    name: "Deputy",
    description: "Deputy is a workforce management solution.",
    logo: "deputy",
    implemented: false,
  },
  {
    id: "destini",
    name: "Destini",
    description: "Destini is a product locator solution for consumers.",
    logo: "destini",
    implemented: false,
  },
  {
    id: "device_magic",
    name: "Device Magic",
    description: "Device Magic is a data collection platform.",
    logo: "device-magic",
    implemented: false,
  },
  {
    id: "dialpad",
    name: "Dialpad",
    description: "Dialpad is an AI-powered business communications platform.",
    logo: "dialpad",
    implemented: false,
  },
  {
    id: "discourse",
    name: "Discourse",
    description: "Discourse is open source discussion platform.",
    logo: "discourse",
    implemented: false,
  },
  {
    id: "dixa",
    name: "Dixa",
    description: "Dixa is a customer service platform.",
    logo: "dixa",
    implemented: false,
  },
  {
    id: "docebo",
    name: "Docebo",
    description: "Docebo is a learning management system.",
    logo: "docebo",
    implemented: false,
  },
  {
    id: "documentdb",
    name: "Amazon DocumentDB",
    description:
      "Amazon DocumentDB is a fully-managed NoSQL database service that is built for JSON data management and integrated with AWS.",
    logo: "mongodb",
    implemented: false,
  },
  {
    id: "donus",
    name: "Donus",
    description: "Donus is a marketing solution for donor development.",
    logo: null,
    implemented: false,
  },
  {
    id: "doorloop",
    name: "DoorLoop",
    description: "DoorLoop is a cloud-based property management platform",
    logo: "doorloop",
    implemented: false,
  },
  {
    id: "double_click_campaign_manager",
    name: "Google Campaign Manager 360",
    description:
      "Google Campaign Manager 360 simplifies advertising campaign management, from media planning to reporting. With all your digital marketing efforts on one powerful platform, you can work smarter, act quicker, and get better results.",
    logo: "google-campaign-manager-360",
    implemented: false,
  },
  {
    id: "double_click_publishers",
    name: "Google Ad Manager",
    description:
      "Google Ad Manager is a hosted ad serving platform to streamline ad management, whether you deliver ads to websites, mobile web pages, mobile apps, games, or a combination. It offers an integrated platform to manage all direct and programmatic sales, and to streamline all ad sales across your web and app inventory.",
    logo: "google-ad-manager",
    implemented: false,
  },
  {
    id: "drata",
    name: "Drata",
    description: "Drata is a security and compliance automation platform.",
    logo: "drata",
    implemented: false,
  },
  {
    id: "drift",
    name: "Drift",
    description:
      "Drift is a sales acceleration platform that provides sales and marking software.",
    logo: "drift",
    implemented: false,
  },
  {
    id: "drip",
    name: "Drip",
    description:
      "Drip is a marketing automation platform for e-commerce businesses.",
    logo: "drip",
    implemented: false,
  },
  {
    id: "dropbox",
    name: "Dropbox",
    description:
      "Dropbox is a cloud storage provider. Fivetran supports syncing files from your Dropbox account to your destination. You can sync multiple files as unique tables to your destination.",
    logo: "dropbox",
    implemented: false,
  },
  {
    id: "dropbox_sign",
    name: "Dropbox Sign",
    description: "Dropbox Sign is an e-signature service.",
    logo: "dropbox-sign",
    implemented: false,
  },
  {
    id: "dsv",
    name: "DSV",
    description: "DSV is a global transport and logistics company.",
    logo: "dsv",
    implemented: false,
  },
  {
    id: "duoplane",
    name: "Duoplane",
    description:
      "Duoplane is a dropship automation and order management software.",
    logo: "duoplane",
    implemented: false,
  },
  {
    id: "dutchie",
    name: "Dutchie",
    description:
      "Dutchie is a unified platform providing e-commerce, POS, payments, and marketing tools to streamline operations for cannabis dispensaries.",
    logo: "dutchie",
    implemented: false,
  },
  {
    id: "dynamics_365",
    name: "Microsoft Dynamics 365 CRM",
    description:
      "Microsoft Dynamics 365 CRM is a cloud-based CRM built by Microsoft for maximum flexibility and extensibility.",
    logo: "microsoft-dynamics365",
    implemented: false,
  },
  {
    id: "dynamodb",
    name: "Amazon DynamoDB",
    description:
      "Amazon DynamoDB is a fully-managed, proprietary NoSQL database service that is offered as part of Amazon Web Services (AWS).",
    logo: "dynamodb",
    implemented: false,
  },
  {
    id: "easypost",
    name: "EasyPost",
    description: "EasyPost is a shipping platform for e-commerce businesses.",
    logo: "easypost",
    implemented: false,
  },
  {
    id: "ebay",
    name: "eBay",
    description: "eBay is an ecommerce marketplace.",
    logo: "ebay",
    implemented: false,
  },
  {
    id: "economic",
    name: "e-conomic",
    description:
      "e-conomic addresses an organization's all bookkeeping needs in one intuitive accounting program.",
    logo: "economic",
    implemented: false,
  },
  {
    id: "elastic_cloud",
    name: "Elastic Cloud",
    description: "Elastic Cloud is a NoSql Document Database",
    logo: "elasticsearch",
    implemented: false,
  },
  {
    id: "electronic_tenant_solutions",
    name: "Electronic Tenant Solutions",
    description:
      "Electronic Tenant Solution is a property management software for commercial real estate industry.",
    logo: "electronic-tenant-solutions",
    implemented: false,
  },
  {
    id: "eloqua",
    name: "Eloqua",
    description:
      "Oracle Eloqua equips marketers with best-in-class lead and campaign management tools that help marketers engage the right audience at the right time in the buyer's journey while providing real-time reporting and insights.",
    logo: "eloqua",
    implemented: false,
  },
  {
    id: "email",
    name: "Email",
    description:
      "Email CSV and JSON files to your destination with the Email Connector. The Email Connector extracts one or more attachments from the email, parses the attachment data and uploads them into your warehouse. Emails are deleted once attachments are extracted from it. This is designed for schedule email reports.",
    logo: "email",
    implemented: false,
  },
  {
    id: "employment_hero",
    name: "Employment Hero",
    description:
      "Employment Hero is a cloud-based human resource and payroll management platform.",
    logo: "employment-hero",
    implemented: false,
  },
  {
    id: "epic_clarity",
    name: "Epic Clarity",
    description: "Epic Clarity is a database for healthcare data.",
    logo: null,
    implemented: false,
  },
  {
    id: "es_self_hosted",
    name: "Self Hosted Elasticsearch",
    description: "Self-Hosted Elasticsearch is a NoSql Document Database",
    logo: "elasticsearch",
    implemented: false,
  },
  {
    id: "eventbrite",
    name: "Eventbrite",
    description: "Eventbrite is an event management and ticketing platform.",
    logo: "eventbrite",
    implemented: false,
  },
  {
    id: "eventsforce",
    name: "Eventsforce",
    description: "Eventsforce is a cloud-based event management platform.",
    logo: "eventsforce",
    implemented: false,
  },
  {
    id: "everflow",
    name: "Everflow",
    description: "Everflow is a partner marketing platform.",
    logo: "everflow",
    implemented: false,
  },
  {
    id: "everhour",
    name: "Everhour",
    description:
      "Everhour is a time-tracking software that can integrate with project management tools.",
    logo: "everhour",
    implemented: false,
  },
  {
    id: "exact_online",
    name: "Exact Online",
    description: "Exact Online is an accounting and CRM software.",
    logo: "exact-online",
    implemented: false,
  },
  {
    id: "expensein",
    name: "ExpenseIn",
    description: "ExpenseIn is an expense management software.",
    logo: "expensein",
    implemented: false,
  },
  {
    id: "expensify",
    name: "Expensify",
    description:
      "Expensify is an expense management tool for personal and business use.",
    logo: "expensify",
    implemented: false,
  },
  {
    id: "ezofficeinventory",
    name: "EZOfficeInventory",
    description:
      "EZOfficeInventory is a tracking software for business assets.",
    logo: "ezofficeinventory",
    implemented: false,
  },
  {
    id: "facebook_ads",
    name: "Facebook Ads",
    description: "Facebook Ads is Meta's online advertising platform.",
    logo: "facebook",
    implemented: true,
  },
  {
    id: "facebook_pages",
    name: "Facebook Pages",
    description:
      "Sync your Facebook pages and posts, as well as insights about them, with the Facebook Pages connector.",
    logo: "facebook",
    implemented: false,
  },
  {
    id: "factbird",
    name: "Factbird",
    description:
      "Factbird is a data gathering and analysis platform for manufacturers.",
    logo: "factbird",
    implemented: false,
  },
  {
    id: "factorial",
    name: "Factorial",
    description: "Factorial is an HR management tool.",
    logo: "factorial",
    implemented: false,
  },
  {
    id: "fathom",
    name: "Fathom",
    description:
      "Fathom is an AI meeting assistant that records, transcribes, highlights, and summarizes meetings, helping individuals and teams focus on the conversation rather than taking notes.",
    logo: null,
    implemented: false,
  },
  {
    id: "fillout",
    name: "Fillout",
    description: "Fillout is a document automation platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "firebase",
    name: "Firebase",
    description:
      "Firebase is an application development platform. Firebase Cloud Firestore is a NoSQL document database characterized by a lack of a fixed schema. Data is stored in key-value pairs in documents that form a collection.",
    logo: "firebase",
    implemented: false,
  },
  {
    id: "fireflies",
    name: "Fireflies",
    description:
      "Fireflies is an AI meeting assistant that records, transcribes, and analyzes your meetings to generate insights, summaries, and searchable transcripts.",
    logo: "fireflies",
    implemented: false,
  },
  {
    id: "firehydrant",
    name: "FireHydrant",
    description: "FireHydrant is an incident management platform.",
    logo: "firehydrant",
    implemented: false,
  },
  {
    id: "fivetran_log",
    name: "Fivetran Platform",
    description:
      "Fivetran generates structured log events from connectors, dashboard user actions, and Fivetran API calls. The Fivetran Platform Connector delivers your logs and account metadata to a schema in your destination. This metadata includes granular Fivetran consumption information.",
    logo: null,
    implemented: false,
  },
  {
    id: "flexport",
    name: "Flexport",
    description: "Flexport is a freight forwarder and logistics platform.",
    logo: "flexport",
    implemented: false,
  },
  {
    id: "float",
    name: "Float",
    description: "Float is a resource management software.",
    logo: null,
    implemented: false,
  },
  {
    id: "flywheel_digital",
    name: "Flywheel Digital",
    description: "Flywheel Digital is a digital commerce platform.",
    logo: "flywheel-digital",
    implemented: false,
  },
  {
    id: "fone_dynamics",
    name: "Fone Dynamics",
    description:
      "Fone Dynamics is a Communications Platform as a Service (CPaaS) platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "forethought",
    name: "Forethought",
    description: "Forethought is an AI-powered customer support platform.",
    logo: "forethought",
    implemented: false,
  },
  {
    id: "forj_community",
    name: "Forj Community",
    description:
      "Forj Community (formerly known as Mobilize) is a community management platform.",
    logo: "forj-community",
    implemented: false,
  },
  {
    id: "formstack",
    name: "Formstack",
    description: "Formstack is a workplace productivity platform.",
    logo: "formstack",
    implemented: false,
  },
  {
    id: "fortnox",
    name: "Fortnox",
    description:
      "Fortnox is a cloud-based platform that helps businesses and accounting firms manage their finances and administration.",
    logo: "fortnox",
    implemented: false,
  },
  {
    id: "fountain",
    name: "Fountain",
    description: "Fountain is a hiring automation software.",
    logo: "fountain",
    implemented: false,
  },
  {
    id: "fourkites",
    name: "FourKites",
    description:
      "FourKites is a real-time tracking and supply chain visibility platform.",
    logo: "fourkites",
    implemented: false,
  },
  {
    id: "freightview",
    name: "Freightview",
    description: "Freightview is an online transportation management system.",
    logo: "freightview",
    implemented: false,
  },
  {
    id: "freshbooks",
    name: "FreshBooks",
    description: "FreshBooks is an accounting software.",
    logo: "freshbooks",
    implemented: false,
  },
  {
    id: "freshchat",
    name: "Freshchat",
    description: "Freshchat is a customer messaging software.",
    logo: "freshchat",
    implemented: false,
  },
  {
    id: "freshdesk",
    name: "Freshdesk",
    description:
      "Freshdesk is a SaaS customer support software and help desk ticketing system. The solution also includes features like Knowledge Base, Community Platform, Live Chat, Phone Support, etc.",
    logo: "freshdesk",
    implemented: false,
  },
  {
    id: "freshdesk_contact_center",
    name: "Freshdesk Contact Center",
    description: "Freshdesk Contact Center is a customer service platform",
    logo: "freshdesk",
    implemented: false,
  },
  {
    id: "freshsales",
    name: "Freshsales",
    description: "Freshsales is a sales CRM software.",
    logo: "freshsales",
    implemented: false,
  },
  {
    id: "freshservice",
    name: "Freshservice",
    description: "Freshservice is a IT service management platform.",
    logo: "freshservice",
    implemented: false,
  },
  {
    id: "freshsuccess",
    name: "Freshsuccess",
    description: "Freshsuccess is a customer success management platform.",
    logo: "freshsuccess",
    implemented: false,
  },
  {
    id: "freshteam",
    name: "FreshTeam",
    description: "FreshTeam is an HR management platform.",
    logo: "freshteam",
    implemented: false,
  },
  {
    id: "friendbuy",
    name: "Friendbuy",
    description:
      "Friendbuy is a referral marketing solution for e-commerce businesses.",
    logo: "friendbuy",
    implemented: false,
  },
  {
    id: "front",
    name: "Front",
    description:
      "Front simplifies how your team manages email lists and customer communications. It provides services to centralize all your communication in one place and optimize how your team handles every message with powerful automations. It also connects your inbox with all the apps you rely on.",
    logo: "front",
    implemented: false,
  },
  {
    id: "ftp",
    name: "FTP",
    description:
      "Fivetran supports syncing files from an FTP server to your destination.",
    logo: "ftp",
    implemented: false,
  },
  {
    id: "fulfil",
    name: "Fulfil",
    description: "Fulfil is a cloud-based ERP platform.",
    logo: "fulfil",
    implemented: true,
  },
  {
    id: "fullstory",
    name: "FullStory",
    description: "FullStory is a digital experience intelligence platform.",
    logo: "fullstory",
    implemented: false,
  },
  {
    id: "g2",
    name: "G2",
    description: "G2 is a software marketplace.",
    logo: "g2",
    implemented: false,
  },
  {
    id: "gainsight_customer_success",
    name: "Gainsight Customer Success",
    description: "Gainsight Customer Success is a customer success platform.",
    logo: "gainsight-product-experience",
    implemented: false,
  },
  {
    id: "gainsight_product_experience",
    name: "Gainsight Product Experience",
    description:
      "Gainsight Product Experience is a product analytics and user engagement platform.",
    logo: "gainsight-product-experience",
    implemented: false,
  },
  {
    id: "gcs",
    name: "Google Cloud Storage",
    description: "Google Cloud Storage is Google's cloud storage system.",
    logo: "google-cloud-storage",
    implemented: false,
  },
  {
    id: "gem",
    name: "Gem",
    description: "Gem is a recruitment platform.",
    logo: "gem",
    implemented: false,
  },
  {
    id: "genesys",
    name: "Genesys",
    description: "Genesys is a customer experience and communication platform.",
    logo: "genesys",
    implemented: false,
  },
  {
    id: "getfeedback",
    name: "GetFeedback",
    description: "GetFeedback is a customer experience and survey platform.",
    logo: "getfeedback",
    implemented: false,
  },
  {
    id: "github",
    name: "GitHub",
    description: "GitHub is a platform for hosting and collaborating on code.",
    logo: "github",
    implemented: true,
  },
  {
    id: "gitlab",
    name: "GitLab",
    description: "GitLab is a web-based Git and DevOps platform.",
    logo: "gitlab",
    implemented: false,
  },
  {
    id: "gladly",
    name: "Gladly",
    description: "Gladly is a customer service platform.",
    logo: "gladly",
    implemented: false,
  },
  {
    id: "globalmeet",
    name: "GlobalMeet",
    description: "GlobalMeet is a virtual events management platform.",
    logo: "globalmeet",
    implemented: false,
  },
  {
    id: "gmail",
    name: "Gmail",
    description: "Gmail is a free email service provided by Google.",
    logo: "gmail",
    implemented: false,
  },
  {
    id: "gocardless",
    name: "GoCardless",
    description:
      "GoCardless is a payment platform that facilitates payment processing for recurring payments.",
    logo: "gocardless",
    implemented: false,
  },
  {
    id: "gofundme_pro",
    name: "GoFundMe Pro",
    description: "GoFundMe Pro is an online fundraising platform.",
    logo: "gofundme-pro",
    implemented: false,
  },
  {
    id: "goldcast",
    name: "Goldcast",
    description: "Goldcast is a B2B platform for virtual and hybrid events.",
    logo: "goldcast",
    implemented: false,
  },
  {
    id: "gongio",
    name: "Gong",
    description: "Gong is a revenue intelligence platform.",
    logo: "gong",
    implemented: false,
  },
  {
    id: "google_ads",
    name: "Google Ads",
    description: "Google Ads is an online advertising platform",
    logo: "google-ads",
    implemented: false,
  },
  {
    id: "google_analytics_4",
    name: "Google Analytics 4",
    description:
      "Google Analytics 4 lets you analyze data about customer engagement with your website or application.",
    logo: "google-analytics-4",
    implemented: false,
  },
  {
    id: "google_analytics_4_export",
    name: "Google Analytics 4 Export",
    description:
      "Google Analytics 4 is an analytics service to measure traffic and engagement across websites and mobile apps.",
    logo: "google-analytics-4",
    implemented: false,
  },
  {
    id: "google_business_profile",
    name: "Google Business Profile",
    description: "Google Business Profile is an online marketing platform.",
    logo: "google",
    implemented: false,
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    description: "Google Calendar is a time management and calendar service.",
    logo: "google-calendar",
    implemented: false,
  },
  {
    id: "google_classroom",
    name: "Google Classroom",
    description:
      "Google Classroom is a digital platform for schools to manage assignments and communication.",
    logo: "google-classroom",
    implemented: false,
  },
  {
    id: "google_cloud_function",
    name: "Google Cloud Functions",
    description:
      "Google Cloud Functions is a serverless computing platform that runs code in response to events and automatically manages the compute resources required by that code.",
    logo: "google",
    implemented: false,
  },
  {
    id: "google_cloud_mysql",
    name: "Google Cloud SQL for MySQL",
    description:
      "Google Cloud SQL for MySQL is a fully-managed MySQL database on Google Cloud Platform.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "google_cloud_postgresql",
    name: "Google Cloud SQL for PostgreSQL",
    description:
      "Google Cloud SQL for Postgres is a hosted version of the popular Postgres database on the Google Cloud platform",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "google_cloud_sqlserver",
    name: "Google Cloud SQL for SQL Server",
    description:
      "Google Cloud SQL is a fully managed SQL Server databases in Google Cloud.",
    logo: "google",
    implemented: false,
  },
  {
    id: "google_display_and_video_360",
    name: "Google Display & Video 360",
    description:
      "Google Display & Video 360 is an integrated solution for end-to-end advertising campaigns.",
    logo: "google-display-and-video-360",
    implemented: false,
  },
  {
    id: "google_drive",
    name: "Google Drive",
    description:
      "Google Drive is a file storage and synchronization service. Fivetran supports syncing files from your Google Drive to your destination. You can sync multiple files as unique tables to your destination.",
    logo: "google-drive",
    implemented: false,
  },
  {
    id: "google_play",
    name: "Google Play",
    description:
      "The Google Play connector will sync monthly reports about individual Android apps to help you track and understand your app's performance. The mid-month reports are updated daily. The full set of reports are described in the Google Play documentation.",
    logo: "google-play",
    implemented: false,
  },
  {
    id: "google_search_ads_360",
    name: "Google Search Ads 360",
    description:
      "helps you monitor and maintain your ad campaigns across multiple search engines using a single centralized tool.",
    logo: "google-search-ads-360",
    implemented: false,
  },
  {
    id: "google_search_console",
    name: "Google Search Console",
    description:
      "Google Search Console helps you monitor and maintain your site's presence in Google Search results.",
    logo: "google",
    implemented: false,
  },
  {
    id: "google_sheets",
    name: "Google Sheets",
    description: "Google Sheets is a web-based spreadsheet application.",
    logo: "sheets",
    implemented: true,
  },
  {
    id: "google_tag_manager",
    name: "Google Tag Manager",
    description:
      "Google Tag Manager is a tag management system that lets you configure and deploy tags on your website or mobile app, from an easy-to-use web interface.",
    logo: "google",
    implemented: false,
  },
  {
    id: "google_tasks",
    name: "Google Tasks",
    description: "Google Tasks is a task management application",
    logo: "google-tasks",
    implemented: false,
  },
  {
    id: "gorgias",
    name: "Gorgias",
    description:
      "Gorgias is a customer support platform for e-commerce businesses.",
    logo: "gorgias",
    implemented: false,
  },
  {
    id: "green_power_monitor",
    name: "GreenPowerMonitor",
    description:
      "GreenPowerMonitor provides monitoring, control, and management solutions for renewable energy assets.",
    logo: "green-power-monitor",
    implemented: false,
  },
  {
    id: "greenhouse",
    name: "Greenhouse",
    description:
      "Greenhouse is an applicant tracking system and recruiting software designed to optimize your entire recruiting process. Find better candidates, conduct more focused interviews, and make data-driven hiring decisions.",
    logo: "greenhouse",
    implemented: false,
  },
  {
    id: "grepsr",
    name: "Grepsr",
    description:
      "Grepsr is a simple, and streamlined web scraping service platform.",
    logo: "grepsr",
    implemented: false,
  },
  {
    id: "grin",
    name: "GRIN",
    description: "GRIN is a creator management platform.",
    logo: "grin",
    implemented: false,
  },
  {
    id: "guru",
    name: "Guru",
    description: "Guru is a data organization platform.",
    logo: "guru",
    implemented: false,
  },
  {
    id: "h_level",
    name: "HighLevel",
    description: "HighLevel is a sales and marketing platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "happyfox",
    name: "HappyFox",
    description:
      "HappyFox is a customer support and ticket management software.",
    logo: "happyfox",
    implemented: false,
  },
  {
    id: "harvest",
    name: "Harvest",
    description: "Harvest is an online time-tracking tool.",
    logo: "harvest",
    implemented: false,
  },
  {
    id: "healthie",
    name: "Healthie",
    description: "Healthie is a platform for digital healthcare providers.",
    logo: "healthie",
    implemented: false,
  },
  {
    id: "heap",
    name: "Heap",
    description:
      "Heap automatically captures every web, mobile, and cloud interaction: clicks, submits, transactions, emails, and more. Retroactively analyze your data without writing code.",
    logo: "heap",
    implemented: false,
  },
  {
    id: "heartland_retail_pos",
    name: "Heartland Retail POS",
    description:
      "Heartland Retail POS is a web-based retail solution for multi-channel and multi-store retailers.",
    logo: "heartland-retail-pos",
    implemented: false,
  },
  {
    id: "helpdesk",
    name: "HelpDesk",
    description: "HelpDesk is a software for customer support activities.",
    logo: "helpdesk",
    implemented: false,
  },
  {
    id: "helpjuice",
    name: "Helpjuice",
    description: "Helpjuice is a knowledge management platform.",
    logo: "helpjuice",
    implemented: false,
  },
  {
    id: "helpscout",
    name: "Help Scout",
    description:
      "Help Scout is a help desk software that provides an email-based customer support.",
    logo: "helpscout",
    implemented: false,
  },
  {
    id: "helpshift",
    name: "Helpshift",
    description: "Helpshift is a customer service platform.",
    logo: "helpshift",
    implemented: false,
  },
  {
    id: "heroku_kafka",
    name: "Heroku Kafka",
    description: "Heroku Kafka is a managed distributed streaming platform.",
    logo: "heroku-kafka",
    implemented: false,
  },
  {
    id: "heroku_postgres",
    name: "Heroku PostgreSQL",
    description:
      "Heroku PostgreSQL is a hosted version of the popular Postgres database on the Heroku platform",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "hex",
    name: "Hex",
    description:
      "Hex is a collaborative platform for data science and analytics.",
    logo: "hex",
    implemented: false,
  },
  {
    id: "hibob",
    name: "HiBob",
    description:
      "HiBob is an HR platform that facilitates management operations.",
    logo: null,
    implemented: false,
  },
  {
    id: "higher_logic_vanilla",
    name: "Higher Logic Vanilla",
    description: "Higher Logic Vanilla is a community engagement platform.",
    logo: "higher-logic-vanilla",
    implemented: false,
  },
  {
    id: "hilti_ontrack",
    name: "Hilti On!Track",
    description:
      "Hilti On!Track is a digital construction asset management platform.",
    logo: "hilti-ontrack",
    implemented: false,
  },
  {
    id: "hopin",
    name: "Hopin",
    description: "Hopin is an online events platform.",
    logo: "hopin",
    implemented: false,
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "HubSpot is an inbound marketing and sales platform.",
    logo: "hubspot",
    implemented: true,
  },
  {
    id: "hvr_agent_plugin_framework",
    name: "HVR Agent Plugin Framework",
    description: "",
    logo: null,
    implemented: false,
  },
  {
    id: "hvr_bigquery",
    name: "HVR BigQuery",
    description: "",
    logo: "bigquery",
    implemented: false,
  },
  {
    id: "hvr_databricks",
    name: "HVR Databricks",
    description: "",
    logo: "databricks",
    implemented: false,
  },
  {
    id: "hvr_db2",
    name: "HVR Db2 for Linux, Unix and Windows",
    description: "",
    logo: "db2-for-linux-unix-and-windows-requirements",
    implemented: false,
  },
  {
    id: "hvr_db2i",
    name: "HVR Db2 for i",
    description: "",
    logo: "db2-for-linux-unix-and-windows-requirements",
    implemented: false,
  },
  {
    id: "hvr_db2z",
    name: "HVR Db2 for z/OS",
    description: "",
    logo: "db2-for-z-os-requirements",
    implemented: false,
  },
  {
    id: "hvr_file_abs",
    name: "HVR Azure Blob Storage",
    description: "",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "hvr_file_adls",
    name: "HVR Azure Data Lake Storage",
    description: "",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "hvr_file_ftp",
    name: "HVR File, FTP, and SFTP",
    description: "",
    logo: "ftp",
    implemented: false,
  },
  {
    id: "hvr_file_gs",
    name: "HVR Google Cloud Storage",
    description: "",
    logo: "google-cloud-storage",
    implemented: false,
  },
  {
    id: "hvr_file_hdfs",
    name: "HVR for Hadoop Distributed File System (HDFS)",
    description: "",
    logo: null,
    implemented: false,
  },
  {
    id: "hvr_file_s3",
    name: "HVR for Amazon S3(Simple Storage Service)",
    description: "hvr_file_s3",
    logo: "amazon-s3",
    implemented: false,
  },
  {
    id: "hvr_file_webdav",
    name: "HVR SharePoint WebDAV",
    description: "",
    logo: null,
    implemented: false,
  },
  {
    id: "hvr_google_spanner",
    name: "HVR Google Spanner",
    description: "",
    logo: "google",
    implemented: false,
  },
  {
    id: "hvr_greenplum",
    name: "HVR Greenplum",
    description: "",
    logo: "greenplum-requirements",
    implemented: false,
  },
  {
    id: "hvr_hana",
    name: "HVR SAP HANA",
    description: "",
    logo: "sap-hana-requirements",
    implemented: false,
  },
  {
    id: "hvr_ingres",
    name: "HVR Ingres",
    description: "",
    logo: "ingres-requirements",
    implemented: false,
  },
  {
    id: "hvr_kafka",
    name: "HVR Kafka",
    description: "",
    logo: "apache-kafka",
    implemented: false,
  },
  {
    id: "hvr_mysql",
    name: "HVR MySQL",
    description: "",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "hvr_mysql_aurora_my",
    name: "HVR Aurora MySQL",
    description: "",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "hvr_mysql_mariadb",
    name: "HVR MariaDB",
    description: "",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "hvr_mysql_tidb",
    name: "HVR MySQL TiDB",
    description: "",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "hvr_netweaver",
    name: "HVR Netweaver",
    description: "",
    logo: "sap-netweaver-requirements",
    implemented: false,
  },
  {
    id: "hvr_netweaver_hana",
    name: "HVR SAP NetWeaver on Hana",
    description: "",
    logo: "sap-hana-requirements",
    implemented: false,
  },
  {
    id: "hvr_netweaver_oracle",
    name: "HVR SAP NetWeaver on Oracle",
    description: "",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "hvr_oracle",
    name: "HVR Oracle",
    description: "",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "hvr_postgresql",
    name: "HVR PostgreSQL",
    description: "",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "hvr_postgresql_aurora_pq",
    name: "HVR Aurora PostgreSQL",
    description: "",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "hvr_redshift",
    name: "HVR Redshift",
    description: "hvr_redshift",
    logo: "redshift",
    implemented: false,
  },
  {
    id: "hvr_singlestore",
    name: "HVR SingleStore",
    description: "",
    logo: "singlestore-requirements",
    implemented: false,
  },
  {
    id: "hvr_snowflake",
    name: "HVR Snowflake",
    description: "",
    logo: "snowflake",
    implemented: false,
  },
  {
    id: "hvr_sqlserver",
    name: "HVR SQL Server",
    description: "",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "hvr_sqlserver_azure",
    name: "HVR Azure SQL Database",
    description: "",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "hvr_sybase",
    name: "HVR Sybase ASE",
    description: "",
    logo: "sybase-ase-requirements",
    implemented: false,
  },
  {
    id: "hvr_teradata",
    name: "HVR Teradata",
    description: "",
    logo: null,
    implemented: false,
  },
  {
    id: "ibacos",
    name: "IBACOS",
    description:
      "IBACOS is passionate about helping builders create better homes that elevate livability, inspire joy and trust, deliver quality and efficiency, and remain resilient, sustainable, and value-driven.",
    logo: null,
    implemented: false,
  },
  {
    id: "ilevel",
    name: "iLevel",
    description: "iLevel is a cloud-based portfolio management platform.",
    logo: "ilevel",
    implemented: false,
  },
  {
    id: "impact",
    name: "Impact Brand",
    description: "Impact Brand is a partnership management platform.",
    logo: "impact-partner",
    implemented: false,
  },
  {
    id: "impact_partner",
    name: "Impact Partner",
    description: "Impact Partner is a partnership management platform.",
    logo: "impact-partner",
    implemented: false,
  },
  {
    id: "incidentio",
    name: "incident.io",
    description: "incident.io is a Slack-integrated incident management tool.",
    logo: "incidentio",
    implemented: false,
  },
  {
    id: "infobip",
    name: "Infobip",
    description: "Infobip is a communication platform for businesses.",
    logo: "infobip",
    implemented: false,
  },
  {
    id: "insightly",
    name: "Insightly",
    description: "Insightly is a customer relationship management software.",
    logo: "insightly",
    implemented: false,
  },
  {
    id: "instagram_business",
    name: "Instagram Business",
    description:
      "Instagram Business provides metadata and insights for IG User (Instagram Business Account) and IG Media (Instagram Photo, Video, Story, or Album) objects.",
    logo: "instagram-business",
    implemented: false,
  },
  {
    id: "integral_ad_science",
    name: "Integral Ad Science",
    description: "Integral Ad Science is a digital ad verification platform.",
    logo: "integral-ad-science",
    implemented: false,
  },
  {
    id: "integrate",
    name: "Integrate",
    description:
      "Integrate is a is a demand marketing and management platform.",
    logo: "integrate",
    implemented: false,
  },
  {
    id: "intercom",
    name: "Intercom",
    description:
      "Intercom brings messaging products for sales, marketing & customer service to one platform, helping businesses avoid the stiff, spammy status quo and have real conversations that build real connections. Designed to feel like the messaging apps you use every day, Intercom lets you talk to consumers almost anywhere: inside your app, on your website, across social media and via email.",
    logo: "intercom",
    implemented: false,
  },
  {
    id: "invoiced",
    name: "Invoiced",
    description:
      "Invoiced is an invoicing and payments solution for B2B companies.",
    logo: null,
    implemented: false,
  },
  {
    id: "ironclad",
    name: "Ironclad",
    description:
      "Ironclad automates contracting processes for legal, sales, and operational sides of businesses.",
    logo: "ironclad",
    implemented: false,
  },
  {
    id: "ironsource",
    name: "ironSource",
    description:
      "ironSource is a business platform for mobile content creators.",
    logo: "ironsource",
    implemented: false,
  },
  {
    id: "iterable",
    name: "Iterable",
    description:
      "Iterable is a growth marketing and customer engagement platform. It enables brands to create, execute and optimize campaigns across email, push, SMS, in-app and more with unparalleled data flexibility.",
    logo: "iterable",
    implemented: false,
  },
  {
    id: "itunes_connect",
    name: "Apple App Store",
    description:
      "Apple App Store (formerly known as iTunes Connect) is a suite of web-based tools created for developers to submit and manage their apps for sale in the App Store or the Mac App Store.",
    logo: "apple-app-store",
    implemented: false,
  },
  {
    id: "ivanti",
    name: "Ivanti",
    description:
      "Ivanti is an enterprise software and service management platform.",
    logo: "ivanti",
    implemented: false,
  },
  {
    id: "jama_software",
    name: "Jama Software",
    description: "Jama Software is a requirements management software.",
    logo: "jama-software",
    implemented: false,
  },
  {
    id: "jamf",
    name: "Jamf",
    description: "Jamf is an enterprise security solution for Apple devices.",
    logo: "jamf",
    implemented: false,
  },
  {
    id: "jibble",
    name: "Jibble",
    description: "Jibble is a productivity monitoring tool.",
    logo: "jibble",
    implemented: false,
  },
  {
    id: "jira",
    name: "Jira",
    description:
      "Jira is a bug tracking, issue tracking and project management software.",
    logo: "jira",
    implemented: false,
  },
  {
    id: "jobnimbus",
    name: "JobNimbus",
    description: "JobNimbus is a CRM and project management software.",
    logo: "jobnimbus",
    implemented: false,
  },
  {
    id: "jotform",
    name: "Jotform",
    description: "Jotform is an application for creating website forms.",
    logo: "jotform",
    implemented: false,
  },
  {
    id: "justcall",
    name: "JustCall",
    description: "JustCall is a customer communication platform.",
    logo: "justcall",
    implemented: false,
  },
  {
    id: "kandji",
    name: "Kandji",
    description: "Kandji is a cloud-based platform for managing Apple devices.",
    logo: "kandji",
    implemented: false,
  },
  {
    id: "katana",
    name: "Katana",
    description: "Katana is a cloud-based Manufacturing ERP (MRP) platform.",
    logo: "katana",
    implemented: false,
  },
  {
    id: "kevel",
    name: "Kevel",
    description: "Kevel is an advertising technology platform.",
    logo: "kevel",
    implemented: false,
  },
  {
    id: "keypay",
    name: "KeyPay",
    description:
      "KeyPay is a cloud-based payroll and workforce management platform.",
    logo: "keypay",
    implemented: false,
  },
  {
    id: "khoros_care",
    name: "Khoros Care",
    description: "Khoros Care is a digital customer engagement platform.",
    logo: "khoros-care",
    implemented: false,
  },
  {
    id: "khoros_communities",
    name: "Khoros Communities",
    description:
      "Khoros Communities is a customer engagement, support, and collaboration platform.",
    logo: "khoros-communities",
    implemented: false,
  },
  {
    id: "khoros_marketing",
    name: "Khoros Marketing",
    description: "Khoros Marketing is a marketing service platform.",
    logo: "khoros-marketing",
    implemented: false,
  },
  {
    id: "kinesis",
    name: "Amazon Kinesis Firehose",
    description:
      "Amazon Kinesis Firehose is a tool for capturing, manipulating, and loading data as it streams from a source to an AWS destination. Upon arrival at an S3 bucket, data from a Firehose delivery stream will appear without newline delimiters in extensionless files. Fivetran supports syncing from an Amazon S3 bucket to your destination json files that originate from a Kinesis Firehose delivery stream.",
    logo: "amazon-kinesis-firehose",
    implemented: false,
  },
  {
    id: "kisi",
    name: "Kisi",
    description: "Kisi is an access control solution.",
    logo: "kisi",
    implemented: false,
  },
  {
    id: "kissmetrics",
    name: "Kissmetrics",
    description:
      "Kissmetrics analyzes the behavior of website visitors to help businesses make product and marketing decisions.",
    logo: "kissmetrics",
    implemented: false,
  },
  {
    id: "klarna",
    name: "Klarna",
    description:
      "Klarna is an e-commerce payment platform for merchants and shoppers.",
    logo: "klarna",
    implemented: false,
  },
  {
    id: "klaviyo",
    name: "Klaviyo",
    description:
      "Klaviyo is an email platform powered by data built for commerce and web businesses.",
    logo: "klaviyo",
    implemented: true,
  },
  {
    id: "konnect_insights",
    name: "Konnect Insights",
    description:
      "Konnect Insights is a customer experience management platform.",
    logo: "konnect-insights",
    implemented: false,
  },
  {
    id: "kustomer",
    name: "Kustomer",
    description:
      "Kustomer is a software platform for customer experience, service, and support integrated with a number of sources to give support staff a complete picture of a customer when they contact the company.",
    logo: "kustomer",
    implemented: false,
  },
  {
    id: "lattice",
    name: "Lattice",
    description: "Lattice is a people management platform.",
    logo: "lattice",
    implemented: false,
  },
  {
    id: "launchdarkly",
    name: "LaunchDarkly",
    description: "LaunchDarkly is a software feature management platform.",
    logo: "launchdarkly",
    implemented: false,
  },
  {
    id: "leap_crm",
    name: "Leap CRM",
    description: "Leap CRM is a job management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "leapsome",
    name: "Leapsome",
    description:
      "Leapsome is a performance management platform that helps organizations align goals, track progress, and improve performance.",
    logo: null,
    implemented: false,
  },
  {
    id: "learn_amp",
    name: "Learn Amp",
    description: "Learn Amp is an employee learning and development platform.",
    logo: "learn-amp",
    implemented: false,
  },
  {
    id: "learnupon",
    name: "LearnUpon",
    description:
      "LearnUpon is a learning management system for training courses and programs.",
    logo: "learnupon",
    implemented: false,
  },
  {
    id: "lemlist",
    name: "Lemlist",
    description:
      "Lemlist is a powerful email outreach and sales engagement platform.",
    logo: "lemlist",
    implemented: false,
  },
  {
    id: "lessonly",
    name: "Lessonly",
    description: "Lessonly by Seismic is a team training software.",
    logo: "lessonly",
    implemented: false,
  },
  {
    id: "lever",
    name: "Lever",
    description:
      "Lever is a Modern Recruiting Software that streamlines the hiring process and simplifies the Applicant Tracking System.",
    logo: "lever",
    implemented: false,
  },
  {
    id: "liftoff",
    name: "Liftoff",
    description: "Liftoff is a mobile app marketing platform.",
    logo: "liftoff",
    implemented: false,
  },
  {
    id: "light_speed_retail",
    name: "Lightspeed Retail",
    description:
      "Lightspeed Retail is a cloud-based point of sale (POS) solution that is suitable for retailers.",
    logo: "light-speed-retail",
    implemented: false,
  },
  {
    id: "lightspeed_retail_xseries",
    name: "Lightspeed Retail X-Series",
    description:
      "Lightspeed Retail X-Series is a cloud-based point of sale (POS) and retail management system.",
    logo: "lightspeed-retail-xseries",
    implemented: false,
  },
  {
    id: "line_ads",
    name: "LINE Ads",
    description:
      "LINE Ads is a is a marketing platform that allows you to create campaigns based on your goals and audience.",
    logo: "line-ads",
    implemented: false,
  },
  {
    id: "linear",
    name: "Linear",
    description: "Linear is an issue tracking and project management tool.",
    logo: "linear",
    implemented: false,
  },
  {
    id: "linkedin_ads",
    name: "LinkedIn Ad Analytics",
    description:
      "LinkedIn Ad Analytics offers you information about the performance of your LinkedIn Ads.",
    logo: "linkedin-ads",
    implemented: false,
  },
  {
    id: "linkedin_company_pages",
    name: "LinkedIn Company Pages",
    description:
      "LinkedIn Company Pages offer you information about the company pages statistics, reactions and social metadata by your LinkedIn organizations.",
    logo: "linkedin-ads",
    implemented: false,
  },
  {
    id: "linksquares",
    name: "LinkSquares",
    description:
      "LinkSquares is a contract lifecycle management platform for legal and finance teams.",
    logo: "linksquares",
    implemented: false,
  },
  {
    id: "listen360",
    name: "Listen360",
    description:
      "Listen360 is a customer experience and reputation management platform.",
    logo: "listen360",
    implemented: false,
  },
  {
    id: "livechat_partner",
    name: "LiveChat Partner",
    description:
      "LiveChat Partner helps handle conversations with the customers.",
    logo: "livechat",
    implemented: false,
  },
  {
    id: "livestorm",
    name: "Livestorm",
    description:
      "Livestorm is a video engagement platform for webinars, virtual events, and online meetings.",
    logo: null,
    implemented: false,
  },
  {
    id: "lob",
    name: "Lob",
    description: "Lob is direct mail platform.",
    logo: "lob",
    implemented: false,
  },
  {
    id: "looker_source",
    name: "Looker",
    description: "Looker is a business intelligence platform.",
    logo: "looker#looker",
    implemented: false,
  },
  {
    id: "loop",
    name: "Loop",
    description: "loop is a return management platform.",
    logo: "loop",
    implemented: false,
  },
  {
    id: "loopio",
    name: "Loopio",
    description: "Loopio is a cloud-based request for proposal (RFP) solution.",
    logo: "loopio",
    implemented: false,
  },
  {
    id: "lucca",
    name: "Lucca",
    description:
      "Lucca is a software solution to automate the HR and administrative processes.",
    logo: null,
    implemented: false,
  },
  {
    id: "luma",
    name: "Luma",
    description: "Luma is an event management solution.",
    logo: "luma",
    implemented: false,
  },
  {
    id: "magento_mysql",
    name: "Magento MySQL",
    description:
      "Magento is an ecommerce platform which provides online merchants with a flexible shopping cart system, and customization of their online store. It offers marketing, search engine optimization, and catalog-management tools. Magento's underlying technology is built on the MySQL database.",
    logo: "magento",
    implemented: false,
  },
  {
    id: "magento_mysql_rds",
    name: "Magento MySQL RDS",
    description:
      "Magento is an ecommerce platform which provides online merchants with a flexible shopping cart system, and customization of their online store. It offers marketing, search engine optimization, and catalog-management tools. Magento's underlying technology is built on the MySQL RDS database.",
    logo: "magento",
    implemented: false,
  },
  {
    id: "mailchimp",
    name: "Mailchimp",
    description:
      "Mailchimp is an email marketing platform to send emails and automated messages, create targeted campaigns, facilitate reporting and analytics, and sell online.",
    logo: "mailchimp",
    implemented: false,
  },
  {
    id: "maileon",
    name: "Maileon",
    description: "Maileon is a marketing automation platform.",
    logo: "maileon",
    implemented: false,
  },
  {
    id: "mailgun",
    name: "Mailgun",
    description: "Mailgun is the intelligent email delivery platform.",
    logo: "mailgun",
    implemented: false,
  },
  {
    id: "mailjet",
    name: "Mailjet",
    description:
      "Mailjet offers an easy-to-use email editor, robust collaboration tools, and professional marketing features to streamline your email needs.",
    logo: "mailjet",
    implemented: false,
  },
  {
    id: "maintainx",
    name: "MaintainX",
    description:
      "MaintainX is a cloud-based Computerized Maintenance Management System (CMMS).",
    logo: null,
    implemented: false,
  },
  {
    id: "malomo",
    name: "Malomo",
    description:
      "Malomo is a shipment tracking and customer experience platform.",
    logo: "malomo",
    implemented: false,
  },
  {
    id: "mambu",
    name: "Mambu",
    description: "Mambu is a cloud banking platform.",
    logo: "mambu",
    implemented: false,
  },
  {
    id: "mandrill",
    name: "Mandrill",
    description: "Mandrill is a transactional email API for Mailchimp users.",
    logo: "mandrill",
    implemented: false,
  },
  {
    id: "maria",
    name: "MariaDB",
    description:
      "MariaDB is an open-source fork of the popular MySQL relational database. Its development is led by some of the original developers of MySQL.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "maria_azure",
    name: "Azure Database for MariaDB",
    description:
      "Azure Database for MariaDB is a hosted version of the popular Maria database on the Azure platform.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "maria_rds",
    name: "Amazon RDS for MariaDB",
    description:
      "Amazon RDS for MariaDB is a fork of the popular MySQL database on Amazon RDS.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "marin",
    name: "Marin Software",
    description:
      "Marin Software is an online advertising platform to measure, manage, and optimize ad spending across the web and mobile devices. Marin provides a Data Passback service which exports reporting data to an SFTP server. Fivetran syncs files from Marin's Data Passback SFTP server to your destination.",
    logo: "marin-software",
    implemented: false,
  },
  {
    id: "marketo",
    name: "Marketo",
    description:
      "Marketo develops marketing automation software that provides inbound marketing, social marketing, CRM, and other related services.",
    logo: "marketo",
    implemented: false,
  },
  {
    id: "matomo",
    name: "Matomo",
    description: "Matomo is a web analytics platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "mavenlink",
    name: "Kantata",
    description:
      "Kantata is an online project management software. Besides project management, it has time tracking, resource planning, collaboration, finance etc. functions.",
    logo: "kantata",
    implemented: false,
  },
  {
    id: "maxio_chargify",
    name: "Maxio Chargify",
    description:
      "Maxio_chargify is a billing and subscription management platform.",
    logo: "maxio-chargify",
    implemented: false,
  },
  {
    id: "maxio_saasoptics",
    name: "Maxio SaaSOptics",
    description:
      "Maxio SaaSOptics is a financial operations platform that enables B2B SaaS teams to manage their go-to-market strategies, calculate revenue, and to generate financial reports.",
    logo: "maxio-saasoptics",
    implemented: false,
  },
  {
    id: "medallia",
    name: "Medallia",
    description: "Medallia is a customer experience management platform.",
    logo: "medallia-agile-research",
    implemented: false,
  },
  {
    id: "medallia_agile_research",
    name: "Medallia Agile Research",
    description:
      "Medallia Agile Research is a self-service research tool to measure and analyze consumer, market, and brand insights.",
    logo: "medallia-agile-research",
    implemented: false,
  },
  {
    id: "megaphone",
    name: "Megaphone",
    description: "Megaphone is a podcast advertising and publishing platform.",
    logo: "megaphone",
    implemented: false,
  },
  {
    id: "mention",
    name: "Mention",
    description: "Mention is a social media monitoring software.",
    logo: "mention",
    implemented: false,
  },
  {
    id: "microsoft_dynamics_365_fno",
    name: "Microsoft Dynamics 365 Finance and Operations",
    description:
      "Microsoft Dynamics 365 Finance and Operations is an ERP solution for large to medium organizations.",
    logo: "microsoft-dynamics365",
    implemented: false,
  },
  {
    id: "microsoft_entra_id",
    name: "Microsoft Entra ID",
    description: "Microsoft Entra ID is a business communication platform.",
    logo: "microsoft",
    implemented: false,
  },
  {
    id: "microsoft_lists",
    name: "Microsoft Lists",
    description:
      "Microsoft Lists is a Microsoft 365 application for work management and tracking.",
    logo: "microsoft",
    implemented: false,
  },
  {
    id: "microsoft_power_bi",
    name: "Microsoft Power BI",
    description: "Microsoft Power BI is a data visualization platform.",
    logo: "microsoft",
    implemented: false,
  },
  {
    id: "microsoft_teams",
    name: "Microsoft Teams",
    description: "Microsoft Teams is a team collaboration application.",
    logo: "microsoft-teams",
    implemented: false,
  },
  {
    id: "missive",
    name: "Missive",
    description: "Missive is a team communication application for businesses.",
    logo: "missive",
    implemented: false,
  },
  {
    id: "mixmax",
    name: "Mixmax",
    description: "Mixmax is a sales engagement platform.",
    logo: "mixmax",
    implemented: false,
  },
  {
    id: "mixpanel",
    name: "Mixpanel",
    description:
      "Mixpanel is a business analytics service that tracks user interactions with web and mobile applications. The data collected is used to build custom reports and measure user engagement and retention.",
    logo: "mixpanel",
    implemented: false,
  },
  {
    id: "mode",
    name: "Mode",
    description: "Mode is a collaborative data platform.",
    logo: "mode",
    implemented: false,
  },
  {
    id: "mollie",
    name: "Mollie",
    description:
      "Mollie offers a single platform for businesses to get paid and manage their money. One that makes payments, reconciliation, reporting, fraud prevention, and financing simple for all from startups to enterprises.",
    logo: null,
    implemented: false,
  },
  {
    id: "moloco",
    name: "Moloco",
    description: "Moloco is a machine learning platform.",
    logo: "moloco",
    implemented: false,
  },
  {
    id: "monday",
    name: "Monday.com",
    description: "Monday.com is a web and mobile work management platform.",
    logo: "monday.com",
    implemented: false,
  },
  {
    id: "mongo",
    name: "MongoDB",
    description:
      "MongoDB is a NoSQL database characterized by a lack of fixed columns and fixed tables. Instead, it has collections (which are similar to tables) and dynamic schemas. MongoDB is a document-oriented database that uses JSON documents.",
    logo: "mongodb",
    implemented: false,
  },
  {
    id: "mongo_sharded",
    name: "MongoDB Sharded",
    description:
      "MongoDB is a NoSQL database characterized by a lack of fixed columns and fixed tables. Instead, it has collections (which are similar to tables) and dynamic schemas. MongoDB is a document-oriented database that uses JSON documents.",
    logo: "mongodb",
    implemented: false,
  },
  {
    id: "mountain",
    name: "MNTN",
    description: "MNTN is a television advertising platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "mux",
    name: "Mux",
    description: "Mux is an API-based video platform.",
    logo: "mux",
    implemented: false,
  },
  {
    id: "myosh",
    name: "myosh",
    description: "myosh is a workplace safety software.",
    logo: null,
    implemented: false,
  },
  {
    id: "mysql",
    name: "MySQL",
    description:
      "MySQL is an open-source relational database. Fivetran's integration platform replicates data from your MySQL source database and loads it into your destination.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "mysql_azure",
    name: "Azure Database for MySQL",
    description:
      "Microsoft Azure Database for MySQL is a high-performance MySQL-compatible database system.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "mysql_rds",
    name: "MySQL RDS",
    description:
      "MySQL RDS is a hosted version of the popular MySQL database on the Amazon RDS platform.",
    logo: "mysql",
    implemented: false,
  },
  {
    id: "namely",
    name: "Namely",
    description: "Namely is an HR platform.",
    logo: "namely",
    implemented: false,
  },
  {
    id: "national_heavy_vehicle_regulator",
    name: "National Heavy Vehicle Regulator",
    description:
      "National Heavy Vehicle Regulator is Australia's regulator for heavy vehicles.",
    logo: null,
    implemented: false,
  },
  {
    id: "navan",
    name: "Navan",
    description: "Navan is an online travel management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "netsuite_suiteanalytics",
    name: "NetSuite SuiteAnalytics",
    description:
      "NetSuite SuiteAnalytics gives businesses the power of built-in real-time reporting and analysis.",
    logo: "netsuite-suiteanalytics",
    implemented: true,
  },
  {
    id: "nice",
    name: "NICE",
    description: "NICE is a customer service and compliance platform.",
    logo: "nice",
    implemented: false,
  },
  {
    id: "notion",
    name: "Notion",
    description: "Notion is a note taking application.",
    logo: "notion",
    implemented: false,
  },
  {
    id: "nylas",
    name: "Nylas",
    description: "Nylas is a communication API platform.",
    logo: "nylas",
    implemented: false,
  },
  {
    id: "odoo",
    name: "Odoo",
    description: "Odoo is an open-source business application suite.",
    logo: "odoo",
    implemented: false,
  },
  {
    id: "okendo",
    name: "Okendo",
    description: "Okendo is a customer marketing platform.",
    logo: "okendo",
    implemented: false,
  },
  {
    id: "okta",
    name: "Okta",
    description: "Okta is an identity management service.",
    logo: "okta",
    implemented: false,
  },
  {
    id: "ometria",
    name: "Ometria",
    description: "Ometria is a customer data experience platform.",
    logo: "ometria",
    implemented: false,
  },
  {
    id: "on24",
    name: "ON24",
    description:
      "ON24 is an engagement platform for businesses to host virtual events, webinars, and digital experiences.",
    logo: "on24",
    implemented: false,
  },
  {
    id: "oncehub",
    name: "OnceHub",
    description:
      "OnceHub is a cloud-based appointment scheduling platform for booking meetings and capturing leads.",
    logo: null,
    implemented: false,
  },
  {
    id: "one_drive",
    name: "OneDrive",
    description:
      "OneDrive is a cloud-based file storage service provided by Microsoft. Fivetran supports syncing files from your OneDrive account to your destination. You can sync multiple files as unique tables to your destination.",
    logo: "one-drive",
    implemented: false,
  },
  {
    id: "onetrust",
    name: "OneTrust",
    description:
      "OneTrust is a cloud platform for privacy, compliance, and data governance.",
    logo: "onetrust",
    implemented: false,
  },
  {
    id: "open_data_dc",
    name: "Open Data DC",
    description:
      "Open Data DC is an open data portal that provides government data for Washington D.C.",
    logo: "open-data-dc",
    implemented: false,
  },
  {
    id: "open_exchange_rates",
    name: "Open Exchange Rates",
    description:
      "Open Exchange Rates is a JSON API that provides live currency exchange rates.",
    logo: "open-exchange-rates",
    implemented: false,
  },
  {
    id: "openai",
    name: "OpenAI",
    description:
      "OpenAI is an artificial intelligence (AI) research and deployment organization.",
    logo: "openai",
    implemented: false,
  },
  {
    id: "opendistro",
    name: "Open Distro",
    description: "Open Distro is a NoSql Document Database",
    logo: "opensearch",
    implemented: false,
  },
  {
    id: "opensearch",
    name: "Opensearch",
    description: "OpenSearch is a NoSql Document Database",
    logo: "opensearch",
    implemented: false,
  },
  {
    id: "optimizely",
    name: "Optimizely",
    description:
      "Optimizely is an experimentation platform, enabling businesses to deliver continuous experimentation and personalization across websites, mobile apps, and connected devices.",
    logo: "optimizely",
    implemented: false,
  },
  {
    id: "oracle",
    name: "Oracle",
    description:
      "Oracle is an object-relational database management system created by the Oracle Corporation. Fivetran's integration platform replicates data from your Oracle source database and loads it into your destination.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "oracle_business_intelligence_publisher",
    name: "Oracle Business Intelligence Publisher",
    description:
      "Oracle Business Intelligence Publisher (BIP) is Oracle's  enterprise reporting product that provides the ability to create and manage  highly formatted reports from a wide range of data sources.",
    logo: "oracle-business-intelligence-publisher",
    implemented: false,
  },
  {
    id: "oracle_ebs",
    name: "Oracle EBS",
    description:
      "Oracle E-Business Suite (EBS) is a set of business applications for managing and automating processes across your organization. Oracle EBS is built on the Oracle database. Instead of pulling data from its API, we pull directly from the source database. Our Oracle E-Business Suite connector shares the limitations of our Oracle database connector.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "oracle_fusion_cloud_apps_crm",
    name: "Oracle Fusion Cloud Applications - CRM",
    description:
      "Oracle Fusion Cloud Applications - CRM is a cloud solution that supports multiple areas of the sales process including customer data management, configure price quote, and subscription management.",
    logo: "oracle-fusion-cloud-applications",
    implemented: false,
  },
  {
    id: "oracle_fusion_cloud_apps_fscm",
    name: "Oracle Fusion Cloud Applications - FSCM(ERP & SCM)",
    description:
      "Oracle Fusion Cloud Applications - FSCM (ERP & SCM) is an Enterprise Resource Planning (ERP) and Supply Chain Management platform covering multiple business areas including, accounting, consolidation, planning, procurement and projects.",
    logo: "oracle-fusion-cloud-applications",
    implemented: false,
  },
  {
    id: "oracle_fusion_cloud_apps_hcm",
    name: "Oracle Fusion Cloud Applications - HCM",
    description:
      "Oracle Fusion Cloud Applications - HCM is a cloud solution that supports multiple people operations in an organization including Human Resources, Talent Management, Workforce Management, and Payroll.",
    logo: "oracle-fusion-cloud-applications",
    implemented: false,
  },
  {
    id: "oracle_fusion_field_service",
    name: "Oracle Fusion Field Service",
    description:
      "Oracle Fusion Field Service is cloud-based field service management solution.",
    logo: "oracle-fusion-field-service",
    implemented: false,
  },
  {
    id: "oracle_hva",
    name: "High-Volume Agent Oracle",
    description:
      "Oracle is an object-relational database management system created by the Oracle Corporation. Fivetran replicates data from your Oracle source database and loads it into your destination using High-Volume Agent connector.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "oracle_moat_analytics",
    name: "Oracle Moat Analytics",
    description:
      "Oracle Moat Analytics is a digital advertising measurement and marketing analytics platform.",
    logo: "oracle-moat-analytics",
    implemented: false,
  },
  {
    id: "oracle_netsuite_openair",
    name: "NetSuite SuiteProjects Pro",
    description:
      "NetSuite SuiteProjects Pro (formerly known as Oracle NetSuite OpenAir) is a cloud-based project management solution.",
    logo: "netsuite-suiteanalytics",
    implemented: false,
  },
  {
    id: "oracle_rac",
    name: "Oracle RAC",
    description: "Oracle Real Application Cluster is a database system.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "oracle_rds",
    name: "Oracle RDS",
    description:
      "Oracle Database RDS is a hosted version of the popular Oracle database on the Amazon RDS platform.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "oracle_sap_hva",
    name: "HVA SAP ECC on Oracle",
    description:
      "SAP ECC on Oracle is an enterprise resource planning and analytics platform built on top of Oracle database. Fivetran replicates SAP data from your Oracle source database and loads it into your destination using High-Volume Agent connector.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "oracle_sap_hva_netweaver",
    name: "HVA SAP ECC on Oracle with NetWeaver",
    description:
      "SAP ECC on Oracle is an enterprise resource planning and analytics platform built on top of Oracle database. Fivetran replicates SAP data from your Oracle source database and loads it into your destination using High-Volume Agent connector.",
    logo: "oracle-ebs",
    implemented: false,
  },
  {
    id: "orbit",
    name: "Orbit",
    description: "Orbit is a community management platform.",
    logo: "orbit",
    implemented: false,
  },
  {
    id: "ordergroove",
    name: "Ordergroove",
    description: "Ordergroove is a subscription management platform.",
    logo: "ordergroove",
    implemented: false,
  },
  {
    id: "ordway",
    name: "Ordway",
    description:
      "Ordway is a cloud-based billing and revenue automation platform.",
    logo: "ordway",
    implemented: false,
  },
  {
    id: "ortto",
    name: "Ortto",
    description:
      "Ortto is a platform for businesses to automate and analyze their messaging and marketing.",
    logo: "ortto",
    implemented: false,
  },
  {
    id: "outbrain",
    name: "Outbrain",
    description:
      "Outbrain is a native advertising platform using targeted advertising to recommend articles, slideshows, blog posts, photos or videos to a reader.",
    logo: "outbrain",
    implemented: false,
  },
  {
    id: "outreach",
    name: "Outreach",
    description:
      "Outreach is a sales engagement platform and automation service.",
    logo: null,
    implemented: false,
  },
  {
    id: "packiyo",
    name: "Packiyo",
    description: "Packiyo is a cloud-based warehouse management system.",
    logo: "packiyo",
    implemented: false,
  },
  {
    id: "paddle",
    name: "Paddle",
    description: "Paddle is a payment infrastructure service.",
    logo: "paddle",
    implemented: false,
  },
  {
    id: "pagerduty",
    name: "PagerDuty",
    description: "PagerDuty is an incident response platform.",
    logo: "pagerduty",
    implemented: false,
  },
  {
    id: "pandadoc",
    name: "PandaDoc",
    description:
      "PandaDoc is a cloud-based end-to-end document management platform.",
    logo: "pandadoc",
    implemented: false,
  },
  {
    id: "papershift",
    name: "Papershift",
    description: "Papershift is a rota planning and staff management software.",
    logo: "papershift",
    implemented: false,
  },
  {
    id: "pardot",
    name: "Pardot",
    description:
      "Pardot is a SaaS-based marketing automation application from Salesforce, enabling marketers to create, deploy and manage online promotion campaigns.",
    logo: "pardot",
    implemented: false,
  },
  {
    id: "pardot_sandbox",
    name: "Pardot Sandbox",
    description:
      "Pardot sandbox is a test version of a Pardot (formerly known as Marketing Cloud Account Engagement) account that is linked to a Salesforce sandbox. It allows you to experiment with and test configurations, data, and integrations in a non-destructive environment without impacting your live production Pardot account or Salesforce data",
    logo: "pardot",
    implemented: false,
  },
  {
    id: "partnerize",
    name: "Partnerize",
    description:
      "Partnerize is a partnership management solution for marketers.",
    logo: "partnerize",
    implemented: false,
  },
  {
    id: "partnerstack_vendor",
    name: "PartnerStack Vendor",
    description:
      "PartnerStack is a platform and software solution designed to help businesses manage and automate their partner and affiliate programs.",
    logo: "partnerstack-vendor",
    implemented: false,
  },
  {
    id: "paychex",
    name: "Paychex",
    description: "Paychex is a human resource, payroll, and benefits platform.",
    logo: "paychex",
    implemented: false,
  },
  {
    id: "paylocity",
    name: "Paylocity",
    description:
      "Paylocity is a payroll and human capital management platform.",
    logo: "paylocity",
    implemented: false,
  },
  {
    id: "paypal",
    name: "PayPal",
    description: "PayPal is an online payment processing platform.",
    logo: "paypal",
    implemented: false,
  },
  {
    id: "paypal_sandbox",
    name: "PayPal Sandbox",
    description:
      "PayPal sandbox is a virtual testing environment that simulates the PayPal production environment.",
    logo: "paypal",
    implemented: false,
  },
  {
    id: "pendo",
    name: "Pendo",
    description:
      "Pendo helps product teams understand and guide users to create product experiences that customers love.",
    logo: "pendo",
    implemented: false,
  },
  {
    id: "peopleforce",
    name: "PeopleForce",
    description:
      "PeopleForce is a cloud-based Human Resource Management System (HRMS).",
    logo: "peopleforce",
    implemented: false,
  },
  {
    id: "persona",
    name: "Persona",
    description: "Persona is an identity management platform.",
    logo: "persona",
    implemented: false,
  },
  {
    id: "personio",
    name: "Personio",
    description: "Personio is an HR management platform.",
    logo: "personio",
    implemented: false,
  },
  {
    id: "phoenix_ads",
    name: "Phoenix Ads",
    description: "Phoenix Ads is a mobile marketing solution provider.",
    logo: null,
    implemented: false,
  },
  {
    id: "picqer",
    name: "Picqer",
    description: "Picqer is an online warehouse management software.",
    logo: "picqer",
    implemented: false,
  },
  {
    id: "pigment",
    name: "Pigment",
    description: "Pigment is a planning and analytics platform.",
    logo: "pigment",
    implemented: false,
  },
  {
    id: "pingdom",
    name: "Pingdom",
    description:
      "Pingdom is a performance and availability monitoring platform for websites, applications, and servers.",
    logo: "pingdom",
    implemented: false,
  },
  {
    id: "pinpoint",
    name: "Pinpoint",
    description:
      "Pinpoint is an applicant tracking software for employers and talent acquisition teams.",
    logo: "pinpoint",
    implemented: false,
  },
  {
    id: "pinterest_ads",
    name: "Pinterest Ads",
    description:
      "Pinterest Ads is an online advertising platform. Ads can be targeted based on user's demographics, search terms, and interests.",
    logo: "pinterest-ads",
    implemented: false,
  },
  {
    id: "pinterest_organic",
    name: "Pinterest Organic",
    description:
      "Pinterest is an online pin board for collecting and sharing multimedia. Fivetran's Pinterest Organic connector syncs data about pins, user accounts, and detailed analytics and metrics from your Pinterest account.",
    logo: "pinterest-organic",
    implemented: false,
  },
  {
    id: "pipe17",
    name: "Pipe17",
    description:
      "Pipe17 is an order management platform for eCommerce businesses.",
    logo: "pipe17",
    implemented: false,
  },
  {
    id: "pipedrive",
    name: "Pipedrive",
    description: "Pipedrive is a sales CRM and pipeline management platform.",
    logo: "pipedrive",
    implemented: false,
  },
  {
    id: "pivotal_tracker",
    name: "Pivotal Tracker",
    description: "Pivotal Tracker is a cloud-based project management tool.",
    logo: "pivotal-tracker",
    implemented: false,
  },
  {
    id: "piwik_pro",
    name: "Piwik PRO",
    description:
      "Piwik PRO is an analytics suite for user privacy and data security.",
    logo: "piwik-pro",
    implemented: false,
  },
  {
    id: "placerai",
    name: "Placer.ai",
    description: "Placer.ai is a location intelligence and analytics solution.",
    logo: "placer-ai",
    implemented: false,
  },
  {
    id: "planetscale",
    name: "PlanetScale",
    description:
      "PlanetScale is a MySQL database platform built on top of Vitess.",
    logo: null,
    implemented: false,
  },
  {
    id: "planful",
    name: "Planful",
    description:
      "Planful is a cloud-based financial performance management software.",
    logo: "planful",
    implemented: false,
  },
  {
    id: "planhat",
    name: "Planhat",
    description: "Planhat is a customer success platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "playvox",
    name: "Playvox",
    description: "Playvox is a quality and workforce management solution.",
    logo: "playvox",
    implemented: false,
  },
  {
    id: "playvox_workforce_management",
    name: "Playvox Workforce Management",
    description:
      "Playvox Workforce Management is a workforce management platform.",
    logo: "playvox-workforce-management",
    implemented: false,
  },
  {
    id: "podio",
    name: "Podio",
    description:
      "Podio is an online platform for developing low-code solutions to customize team communication, business processes, and project management.",
    logo: "podio",
    implemented: false,
  },
  {
    id: "poplar",
    name: "Poplar",
    description: "Poplar is a direct mail platform.",
    logo: "poplar",
    implemented: false,
  },
  {
    id: "postgres",
    name: "PostgreSQL",
    description: "PostgreSQL is a database system",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "postgres_rds",
    name: "Amazon RDS for PostgreSQL",
    description:
      "Amazon RDS for PostgreSQL is a hosted version of the popular Postgres database on the Amazon RDS platform",
    logo: "postgresql",
    implemented: false,
  },
  {
    id: "posthog",
    name: "PostHog",
    description: "PostHog is an open-source product analytics platform.",
    logo: "posthog",
    implemented: true,
  },
  {
    id: "postmark",
    name: "Postmark",
    description: "Postmark is an email delivery service.",
    logo: "postmark",
    implemented: false,
  },
  {
    id: "power_reviews_enterprise",
    name: "PowerReviews Enterprise",
    description:
      "PowerReviews Enterprise is a software platform for product reviews and ratings.",
    logo: null,
    implemented: false,
  },
  {
    id: "prisma_cloud",
    name: "Prisma Cloud",
    description: "Prisma Cloud is a cloud native security platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "prive",
    name: "Prive",
    description: "Prive is a revenue platform for commerce.",
    logo: "prive",
    implemented: false,
  },
  {
    id: "procore",
    name: "Procore",
    description: "Procore is a construction management software.",
    logo: null,
    implemented: false,
  },
  {
    id: "productboard",
    name: "Productboard",
    description: "Productboard is a customer-driven product management system.",
    logo: "productboard",
    implemented: false,
  },
  {
    id: "productive",
    name: "Productive",
    description: "Productive is a project management software for agencies.",
    logo: "productive",
    implemented: false,
  },
  {
    id: "proofpoint_security_awareness",
    name: "Proofpoint Security Awareness",
    description:
      "Proofpoint Security Awareness is a cloud-based security platform.",
    logo: "proofpoint-security-awareness",
    implemented: false,
  },
  {
    id: "prosperstack",
    name: "ProsperStack",
    description:
      "ProsperStack is a customer acquisition and retention platform for subscription-based businesses.",
    logo: "prosperstack",
    implemented: false,
  },
  {
    id: "purple_dot",
    name: "Purple Dot",
    description: "Purple Dot is a pre-order management platform.",
    logo: "purple-dot",
    implemented: false,
  },
  {
    id: "pylon_support_platform",
    name: "Pylon Support Platform",
    description: "Pylon Support Platform is a customer support platform.",
    logo: "pylon-support-platform",
    implemented: false,
  },
  {
    id: "qmatic_data_connect",
    name: "Qmatic Data Connect",
    description:
      "Qmatic Data Connect provides insights into customer journey data.",
    logo: "qmatic-data-connect",
    implemented: false,
  },
  {
    id: "qualaroo",
    name: "Qualaroo",
    description: "Qualaroo is a cloud-based feedback management tool.",
    logo: "qualaroo",
    implemented: false,
  },
  {
    id: "qualtrics",
    name: "Qualtrics",
    description:
      "Qualtrics is an online survey tool that allows you to build and distribute surveys, and analyze responses.",
    logo: "qualtrics",
    implemented: false,
  },
  {
    id: "quickbooks",
    name: "QuickBooks",
    description:
      "QuickBooks is Intuit Inc's set of software solutions designed to manage payroll, inventory, sales, and other needs of a small business. The software's features include marketing tools, merchant services, product and supplies training solutions.",
    logo: "quickbooks",
    implemented: true,
  },
  {
    id: "quora_ads",
    name: "Quora Ads",
    description: "Quora Ads is an advertising platform.",
    logo: "quora-ads",
    implemented: false,
  },
  {
    id: "quorum",
    name: "Quorum",
    description: "Quorum is a public affairs software.",
    logo: "quorum",
    implemented: false,
  },
  {
    id: "rakutenadvertising",
    name: "Rakuten Advertising",
    description:
      "Rakuten Advertising is a digital advertising and marketing solutions platform.",
    logo: "rakuten-advertising",
    implemented: false,
  },
  {
    id: "ramp",
    name: "Ramp",
    description: "Ramp is a corporate card and spend management platform.",
    logo: "ramp",
    implemented: false,
  },
  {
    id: "rarible",
    name: "Rarible",
    description:
      "Rarible is a marketplace for issuing and selling custom crypto assets.",
    logo: "rarible",
    implemented: false,
  },
  {
    id: "rebound_returns",
    name: "ReBound Returns",
    description: "ReBound Returns is a global returns management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "recharge",
    name: "Recharge",
    description:
      "Recharge provides a recurring billing and subscriptions solution for ecommerce.",
    logo: "recharge",
    implemented: false,
  },
  {
    id: "recurly",
    name: "Recurly",
    description:
      "Recurly provides recurring billing management as an outsourced service.",
    logo: "recurly",
    implemented: false,
  },
  {
    id: "reddit_ads",
    name: "Reddit Ads",
    description:
      "Reddit Ads is an online advertising platform where advertisers bid to display promoted posts sprinkled in with regular Reddit posts.",
    logo: "reddit-ads",
    implemented: false,
  },
  {
    id: "referralhero",
    name: "ReferralHero",
    description: "ReferralHero is a referral program software.",
    logo: "referralhero",
    implemented: false,
  },
  {
    id: "refiner",
    name: "Refiner",
    description: "Refiner is a customer survey platform.",
    logo: "refiner",
    implemented: false,
  },
  {
    id: "reltio",
    name: "Reltio",
    description: "Reltio is a cloud-native master data management platform.",
    logo: "reltio",
    implemented: false,
  },
  {
    id: "replyio",
    name: "Reply.io",
    description: "Reply.io is a multi-channel sales engagement platform.",
    logo: "reply.io",
    implemented: false,
  },
  {
    id: "resource_management_by_smartsheet",
    name: "Resource Management by Smartsheet",
    description:
      "Resource Management by Smartsheet is a workforce management platform..",
    logo: "resource-management-by-smartsheet",
    implemented: false,
  },
  {
    id: "retailnext",
    name: "RetailNext",
    description: "RetailNext is an in-store retail analytics platform.",
    logo: "retailnext",
    implemented: false,
  },
  {
    id: "retently",
    name: "Retently",
    description: "Retently is a customer experience management platform.",
    logo: "retently",
    implemented: false,
  },
  {
    id: "revel",
    name: "Revel Systems",
    description:
      "Revel Systems is a point of sale (POS) and business management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "revenuecat",
    name: "RevenueCat",
    description:
      "RevenueCat is a platform for managing subscriptions, purchases, and user data across platforms.",
    logo: "revenuecat",
    implemented: false,
  },
  {
    id: "reviewsai",
    name: "REVIEWS.ai",
    description: "REVIEWS.ai is a customer feedback platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "reviewsio",
    name: "REVIEWS.io",
    description: "REVIEWS.io is an online review management platform.",
    logo: "reviews.io",
    implemented: false,
  },
  {
    id: "revops",
    name: "RevOps",
    description: "RevOps is a deal desk platform for revenue operations.",
    logo: null,
    implemented: false,
  },
  {
    id: "revx",
    name: "RevX",
    description:
      "RevX is a programmatic advertising platform for app marketers.",
    logo: "revx",
    implemented: false,
  },
  {
    id: "ricochet360",
    name: "Ricochet360",
    description:
      "Ricochet360 provides automatic dialing, customer relationship management, and marketing tools for companies.",
    logo: "ricochet360",
    implemented: false,
  },
  {
    id: "ringcentral",
    name: "RingCentral",
    description:
      "RingCentral is a cloud-based business communications solutions platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "ringover",
    name: "Ringover",
    description:
      "Ringover is a SaaS solution for enterprises to manage their communications with clients and prospects.",
    logo: "ringover",
    implemented: false,
  },
  {
    id: "rippling",
    name: "Rippling",
    description: "Rippling is a cloud-based workforce management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "rithum",
    name: "Rithum",
    description: "Rithum is a cloud-based e-commerce platform.",
    logo: "rithum",
    implemented: false,
  },
  {
    id: "rivo",
    name: "Rivo",
    description:
      "Rivo is the Shopify-based Rivo Loyalty application that enables merchants to run customer loyalty and rewards programs.",
    logo: null,
    implemented: false,
  },
  {
    id: "rocketlane",
    name: "Rocketlane",
    description:
      "Rocketlane is a software for customer onboarding and professional services automation.",
    logo: "rocketlane",
    implemented: false,
  },
  {
    id: "rokt",
    name: "Rokt",
    description:
      "Rokt is a software platform that personalizes customer interactions during transactions.",
    logo: "rokt",
    implemented: false,
  },
  {
    id: "rollbar",
    name: "Rollbar",
    description:
      "Rollbar is a cloud-based code improvement solution to monitor and debug errors in your application code.",
    logo: "rollbar",
    implemented: false,
  },
  {
    id: "rootly",
    name: "Rootly",
    description: "Rootly is an incident management platform.",
    logo: "rootly",
    implemented: false,
  },
  {
    id: "rtb_house",
    name: "RTB House",
    description: "RTB House is an online marketing technologies solution.",
    logo: "rtb-house",
    implemented: false,
  },
  {
    id: "ruddr",
    name: "Ruddr",
    description: "Ruddr is a professional services automation platform.",
    logo: "ruddr",
    implemented: false,
  },
  {
    id: "rundeck",
    name: "Rundeck",
    description: "Rundeck is an automation orchestration platform.",
    logo: "rundeck",
    implemented: false,
  },
  {
    id: "s3",
    name: "Amazon S3",
    description:
      "Amazon Simple Storage Service (Amazon S3), provides scalable cloud storage services. Fivetran supports syncing files from your S3 bucket to your destination.",
    logo: "amazon-s3",
    implemented: false,
  },
  {
    id: "s3_compatible_storage",
    name: "S3-Compatible Storage",
    description:
      "A storage system is considered S3-compatible if it supports the standard Amazon S3 REST API. Fivetran supports syncing files from your S3-Compatible Storage bucket to your destination.",
    logo: "s3-compatible-storage",
    implemented: false,
  },
  {
    id: "safebase",
    name: "SafeBase",
    description: "SafeBase is a trust center platform for security reviews.",
    logo: null,
    implemented: false,
  },
  {
    id: "safetyculture",
    name: "SafetyCulture",
    description: "SafetyCulture is a workplace safety management solution.",
    logo: null,
    implemented: false,
  },
  {
    id: "sage_hr",
    name: "Sage HR",
    description: "Sage HR is a human resource management solution.",
    logo: "sage-hr",
    implemented: false,
  },
  {
    id: "sage_intacct",
    name: "Sage Intacct",
    description:
      "Sage Intacct is a provider of cloud-based accounting software which can provide real-time financial and operational insights, as well as the ability to automate critical processes.",
    logo: "sage-intacct",
    implemented: false,
  },
  {
    id: "sailthru",
    name: "Sailthru",
    description:
      "Sailthru provides services for email management. Sailthru services include scheduling and sending campaigns and transactional emails to each user's interest. Campaigns are email messages scheduled for group of users based on a specific template. It can contain news update or promotional messages. Transactional emails are sent to a particular user in response to a specific action.",
    logo: "sailthru",
    implemented: false,
  },
  {
    id: "salesforce",
    name: "Salesforce",
    description: "Salesforce is a cloud-based CRM platform.",
    logo: "salesforce",
    implemented: true,
  },
  {
    id: "salesforce_commerce_cloud",
    name: "Salesforce Commerce Cloud",
    description:
      "Salesforce Commerce is an ecommerce platform that provides unified, intelligent digital commerce experiences, both online and in the store.",
    logo: "salesforce",
    implemented: false,
  },
  {
    id: "salesforce_marketing_cloud",
    name: "Salesforce Marketing Cloud",
    description:
      "Salesforce Marketing Cloud provides digital marketing automation, analytics, and lead management for B2B and B2C companies.",
    logo: "salesforce",
    implemented: false,
  },
  {
    id: "salesforce_sandbox",
    name: "Salesforce Sandbox",
    description: "Salesforce is a cloud-based CRM platform.",
    logo: "salesforce",
    implemented: false,
  },
  {
    id: "salesloft",
    name: "Salesloft",
    description: "Salesloft is a sales engagement platform.",
    logo: "salesloft",
    implemented: false,
  },
  {
    id: "salsify",
    name: "Salsify",
    description: "Salsify is a product experience management tool.",
    logo: "salsify",
    implemented: false,
  },
  {
    id: "samsara",
    name: "Samsara",
    description:
      "Samsara develops sensor systems with remote networking and cloud-based analytics.",
    logo: "samsara",
    implemented: false,
  },
  {
    id: "sana",
    name: "Sana",
    description: "Sana is a learning platform.",
    logo: "sana",
    implemented: false,
  },
  {
    id: "sap_business_by_design",
    name: "SAP Business ByDesign",
    description:
      "SAP Business ByDesign is a cloud-based enterprise resource planning system for small and medium-sized organizations.",
    logo: "sap",
    implemented: false,
  },
  {
    id: "sap_hana_db",
    name: "SAP ERP on HANA",
    description:
      "SAP Enterprise Resource Planning (ERP) applications like SAP ECC and SAP S/4 HANA manage customer finance,  operations, supply chain and HR processes. With the SAP ERP on HANA connector,  Fivetran offers the possibility to replicate data from SAP ECC and S/4 HANA systems running on a HANA platform. [SAP HANA](https://www.sap.com/products/hana.html) is an in-memory, column-oriented, relational database management system.  Fivetran replicates data from your SAP HANA source database and loads it into your destination.",
    logo: "sap-hana-requirements",
    implemented: false,
  },
  {
    id: "sap_odata",
    name: "SAP OData",
    description:
      "SAP OData API (Open Data Protocol) is a standardized protocol used to create and consume RESTful APIs.   It allows for the integration of data between SAP systems and external applications. SAP OData API is designed to work seamlessly with SAP systems. It allows developers to expose data from SAP ERP, SAP S/4HANA, and other SAP   products through OData services.",
    logo: "sap-odata",
    implemented: false,
  },
  {
    id: "sap_success_factors",
    name: "SAP SuccessFactors",
    description:
      "SAP SuccessFactors is a cloud-based human capital management software.",
    logo: "sap-success-factors",
    implemented: false,
  },
  {
    id: "scorm",
    name: "SCORM",
    description:
      "SCORM (Sharable Content Object Reference Model) is a set of technical standards for eLearning products.",
    logo: "scorm",
    implemented: false,
  },
  {
    id: "security_journey",
    name: "Security Journey",
    description:
      "Security Journey is a software development security training platform.",
    logo: "security-journey",
    implemented: false,
  },
  {
    id: "segment",
    name: "Segment",
    description: "Segment is an event tracking library.",
    logo: "segment",
    implemented: false,
  },
  {
    id: "sendgrid",
    name: "SendGrid",
    description:
      "SendGrid is a customer communication platform for transactional and marketing emails.",
    logo: "sendgrid",
    implemented: false,
  },
  {
    id: "sentry",
    name: "Sentry",
    description: "Sentry is an application monitoring software.",
    logo: "sentry",
    implemented: false,
  },
  {
    id: "servicenow",
    name: "ServiceNow",
    description:
      "ServiceNow is a cloud platform that provides service management software as a service.",
    logo: "servicenow",
    implemented: false,
  },
  {
    id: "servicetitan",
    name: "ServiceTitan",
    description:
      "ServiceTitan is a platform for service companies to manage their customers, operations, finances, and growth.",
    logo: "servicetitan",
    implemented: false,
  },
  {
    id: "servicetrade",
    name: "ServiceTrade",
    description: "ServiceTrade is a field service management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "sftp",
    name: "SFTP",
    description:
      "Fivetran supports syncing files from your SSH server to your destination using SFTP.",
    logo: "sftp",
    implemented: false,
  },
  {
    id: "share_point",
    name: "SharePoint",
    description:
      "SharePoint is a file storage and collaboration service. Fivetran supports syncing files and lists from your SharePoint site to your destination. You can sync multiple files and lists as unique tables to your destination.",
    logo: "microsoft",
    implemented: false,
  },
  {
    id: "shareasale",
    name: "ShareASale",
    description: "ShareASale is a performance marketing network.",
    logo: "shareasale",
    implemented: false,
  },
  {
    id: "sharetribe",
    name: "Sharetribe",
    description: "Sharetribe is a marketplace building platform.",
    logo: "sharetribe",
    implemented: false,
  },
  {
    id: "shiphero",
    name: "ShipHero",
    description: "ShipHero is a cloud-based warehouse management solution.",
    logo: "shiphero",
    implemented: false,
  },
  {
    id: "shipmonk",
    name: "Shipmonk",
    description:
      "Shipmonk is a technology-driven third-party logistics (3PL) provider that enables e-commerce brands, subscription boxes, and retail businesses to manage inventory, streamline order fulfillment, and optimize shipping.",
    logo: null,
    implemented: false,
  },
  {
    id: "shipnetwork",
    name: "ShipNetwork",
    description: "ShipNetwork is a shipment and order fulfilment platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "shipstation",
    name: "ShipStation",
    description: "ShipStation is an ecommerce shipping platform.",
    logo: "shipstation",
    implemented: false,
  },
  {
    id: "shopify",
    name: "Shopify",
    description:
      "Shopify is an ecommerce platform that has everything you need to sell online, on social media, or in person.",
    logo: "shopify",
    implemented: true,
  },
  {
    id: "shopware",
    name: "Shopware",
    description:
      "Shopware is an open-source platform for creating and marketing e-commerce businesses.",
    logo: "shopware",
    implemented: false,
  },
  {
    id: "shortcut",
    name: "Shortcut",
    description:
      "Shortcut is a project management and issue tracking platform.",
    logo: "shortcut",
    implemented: false,
  },
  {
    id: "shortio",
    name: "Short.io",
    description: "Short.io is a URL shortener platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "showpad",
    name: "Showpad",
    description: "Showpad is a sales enablement platform.",
    logo: "showpad",
    implemented: false,
  },
  {
    id: "siemens_building_x_openness",
    name: "Siemens Building X Openness",
    description:
      "Siemens Building X Openness is a cloud-based platform to manage building operations.",
    logo: "siemens-building-x-openness",
    implemented: false,
  },
  {
    id: "sigma_computing_source",
    name: "Sigma Computing",
    description:
      "Sigma Computing is a cloud-native analytics platform for instant insights.",
    logo: "sigma-computing",
    implemented: false,
  },
  {
    id: "simplecast",
    name: "Simplecast",
    description: "Simplecast is a podcast hosting and analytics platform.",
    logo: "simplecast",
    implemented: false,
  },
  {
    id: "simplesat",
    name: "Simplesat",
    description: "Simplesat is a customer feedback and survey platform.",
    logo: "simplesat",
    implemented: false,
  },
  {
    id: "singlestore_source",
    name: "SingleStore",
    description:
      "SingleStore is a cloud-native database designed for data-intensive applications",
    logo: "singlestore-requirements",
    implemented: false,
  },
  {
    id: "sistrix",
    name: "SISTRIX",
    description: "SISTRIX is an SEO visibility and analysis platform.",
    logo: "sistrix",
    implemented: false,
  },
  {
    id: "skilljar",
    name: "Skilljar",
    description: "Skilljar is a customer training platform.",
    logo: "skilljar",
    implemented: false,
  },
  {
    id: "skillstx",
    name: "SkillsTX",
    description: "SkillsTX is a digital skill management platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "skimlinks",
    name: "Skimlinks",
    description: "Skimlinks is a commerce content monetization platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "skuvault",
    name: "SkuVault",
    description:
      "SkuVault is a cloud-based inventory and warehouse management platform.",
    logo: "skuvault",
    implemented: false,
  },
  {
    id: "slab",
    name: "Slab",
    description:
      "Slab is a knowledge base for organizing and managing documentation.",
    logo: "slab",
    implemented: false,
  },
  {
    id: "slack_admin_data",
    name: "Slack Admin Data",
    description:
      "Slack is a messaging program designed specifically for the workplace.",
    logo: "slack",
    implemented: false,
  },
  {
    id: "slack_app",
    name: "Slack",
    description:
      "Slack is a messaging program designed specifically for the workplace.",
    logo: "slack",
    implemented: false,
  },
  {
    id: "smadex",
    name: "Smadex",
    description:
      "Smadex is a software solution for user acquisition, retargeting, and branding campaigns.",
    logo: null,
    implemented: false,
  },
  {
    id: "smarthr",
    name: "SmartHR",
    description: "SmartHR is a cloud-based HR management platform.",
    logo: "smarthr",
    implemented: false,
  },
  {
    id: "smartrecruiters",
    name: "SmartRecruiters",
    description: "SmartRecruiters is a recruiting platform.",
    logo: "smartrecruiters",
    implemented: false,
  },
  {
    id: "smartsheet",
    name: "Smartsheet",
    description:
      "Spreadsheet application for collaboration and work management",
    logo: "smartsheet",
    implemented: false,
  },
  {
    id: "smartwaiver",
    name: "Smartwaiver",
    description: "Smartwaiver is a cloud-based digital waiver solution.",
    logo: "smartwaiver",
    implemented: false,
  },
  {
    id: "snapchat_ads",
    name: "Snapchat Ads",
    description:
      "Snapchat Ads provides a platform to create, manage, and measure your ad campaigns. You can find more information on Snapchat documentation. Snapchat Ads connector provides a history of the settings and detailed reports of your Snapchat Ad Accounts, Campaigns, Ad Squads, and Media Creatives.",
    logo: "snapchat-ads",
    implemented: false,
  },
  {
    id: "snowflake_db",
    name: "Snowflake",
    description: "Cloud Database Service (https://www.snowflake.com/)",
    logo: "snowflake",
    implemented: false,
  },
  {
    id: "snowplow",
    name: "Snowplow",
    description:
      "Snowplow Analytics is an event tracking platform that allows you to track events across all channels and action the data in real-time. Snowplow uses trackers to track your information in whichever language your project is built.",
    logo: "snowplow",
    implemented: false,
  },
  {
    id: "snyk",
    name: "Snyk",
    description: "Snyk is a software security platform.",
    logo: "snyk",
    implemented: false,
  },
  {
    id: "solarwinds_service_desk",
    name: "SolarWinds Service Desk",
    description:
      "SolarWinds Service Desk is an IT service management (ITSM) platform.",
    logo: "solarwinds-service-desk",
    implemented: false,
  },
  {
    id: "sonarqube",
    name: "SonarQube",
    description:
      "SonarQube is an open-source platform for continuous inspection of code quality.",
    logo: "sonarqube",
    implemented: false,
  },
  {
    id: "sparkpost",
    name: "SparkPost",
    description: "SparkPost is a cloud-based email delivery service.",
    logo: "sparkpost",
    implemented: false,
  },
  {
    id: "splash",
    name: "Splash",
    description: "Splash is an event marketing platform.",
    logo: "splash",
    implemented: false,
  },
  {
    id: "splitio",
    name: "Split",
    description: "Split is a feature delivery platform.",
    logo: "split",
    implemented: false,
  },
  {
    id: "splunk",
    name: "Splunk",
    description:
      "Splunk is a software platform that searches, analyzes and visualizes the machine-generated data gathered from websites, applications, sensors, and devices.",
    logo: "splunk",
    implemented: false,
  },
  {
    id: "spotify_ads",
    name: "Spotify Ads",
    description: "The Spotify Ads lets get reports on campaigns",
    logo: "spotify-ads",
    implemented: false,
  },
  {
    id: "sprout",
    name: "Sprout Social",
    description:
      "Sprout Social is a social media business management platform.",
    logo: "sprout-social",
    implemented: false,
  },
  {
    id: "sql_server",
    name: "SQL Server",
    description:
      "SQL Server is Microsoft's SQL database. Fivetran's integration service replicates data from your SQL Server source database and loads it into your destination at regular intervals.",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "sql_server_hva",
    name: "High-Volume Agent SQL Server",
    description:
      "SQL Server is Microsoft's SQL database. Fivetran replicates data from your SQL Server source database and loads it into your destination at regular intervals using the High-Volume Agent connector.",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "sql_server_rds",
    name: "Amazon RDS for SQL Server",
    description:
      "Amazon RDS for SQL Server is a hosted version of the popular Microsoft SQL Server database on the Amazon RDS platform.",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "sql_server_sap_ecc_hva",
    name: "HVA SAP ECC on SQL Server",
    description:
      "SAP ECC on SQL Server is an enterprise resource planning and analytics platform built on top of SQL Server database. Fivetran replicates SAP data from your SQL Server source database and loads it into your destination using the High-Volume Agent connector.",
    logo: "azure-blob-storage",
    implemented: false,
  },
  {
    id: "square",
    name: "Square",
    description:
      "Square, Inc. is a financial services, merchant services aggregator, and mobile payment company.",
    logo: "square",
    implemented: false,
  },
  {
    id: "squarespace",
    name: "Squarespace",
    description: "Squarespace is a platform for building and hosting websites.",
    logo: "squarespace",
    implemented: false,
  },
  {
    id: "stackadapt_graphql",
    name: "StackAdapt",
    description: "StackAdapt is a marketing platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "standard_metrics",
    name: "Standard Metrics",
    description:
      "Standard Metrics is a cloud-based data platform for gathering and analyzing financial data.",
    logo: "standard-metrics",
    implemented: false,
  },
  {
    id: "starrez",
    name: "StarRez",
    description: "StarRez is a student property management platform.",
    logo: "starrez",
    implemented: false,
  },
  {
    id: "statistics_netherlands_cbs",
    name: "Statistics Netherlands CBS",
    description:
      "Statistics Netherlands (CBS) is a Dutch governmental institution that gathers statistical information about the Netherlands.",
    logo: "statistics-netherlands-cbs",
    implemented: false,
  },
  {
    id: "statsig",
    name: "Statsig",
    description: "Statsig is a product observability platform.",
    logo: "statsig",
    implemented: false,
  },
  {
    id: "statuspage",
    name: "Statuspage",
    description: "Statuspage is an incident management platform.",
    logo: "statuspage",
    implemented: false,
  },
  {
    id: "stickyio",
    name: "sticky.io",
    description: "sticky.io is a payments engine and CRM solution.",
    logo: null,
    implemented: false,
  },
  {
    id: "stripe",
    name: "Stripe",
    description:
      "Stripe is a credit card payment system that helps you track recurring charges",
    logo: "stripe",
    implemented: true,
  },
  {
    id: "stripe_test",
    name: "Stripe test mode",
    description: "Stripe is an online payment processing platform.",
    logo: "stripe",
    implemented: false,
  },
  {
    id: "stylight",
    name: "Stylight",
    description:
      "Stylight is a fashion and lifestyle platform that allows users to discover and shop for the latest trends and products from a wide range of brands and retailers.",
    logo: "stylight",
    implemented: false,
  },
  {
    id: "subscript",
    name: "Subscript",
    description:
      "Subscript is a subscription intelligence platform that helps finance teams in tracking and comprehending subscription metrics",
    logo: "subscript",
    implemented: false,
  },
  {
    id: "sugarcrm",
    name: "SugarCRM",
    description:
      "SugarCRM is a customer relationship management (CRM) platform.",
    logo: "sugarcrm",
    implemented: false,
  },
  {
    id: "survey_monkey",
    name: "SurveyMonkey",
    description:
      "SurveyMonkey is an online survey development tool to create and run surveys.",
    logo: "surveymonkey",
    implemented: false,
  },
  {
    id: "survicate",
    name: "Survicate",
    description:
      "Survicate is a survey tool that can gather data on a user's experience across a platform.",
    logo: "survicate",
    implemented: false,
  },
  {
    id: "swoogo",
    name: "Swoogo",
    description: "Swoogo is an event management platform.",
    logo: "swoogo",
    implemented: false,
  },
  {
    id: "tableau_source",
    name: "Tableau",
    description: "Tableau is a data visualization platform.",
    logo: "tableau",
    implemented: false,
  },
  {
    id: "taboola",
    name: "Taboola",
    description:
      "Taboola develops and markets a service for online content publishers and advertisers that recommends digital content to website users. You can find more information on Taboola documentation.",
    logo: "taboola",
    implemented: false,
  },
  {
    id: "talkdesk",
    name: "Talkdesk",
    description: "Talkdesk is a cloud-based call center solution.",
    logo: "talkdesk",
    implemented: false,
  },
  {
    id: "talkwalker",
    name: "Talkwalker",
    description: "Talkwalker is a consumer intelligence platform.",
    logo: "talkwalker",
    implemented: false,
  },
  {
    id: "teads",
    name: "Teads",
    description:
      "Teads is a platform that connects advertisers with publishers to deliver ads.",
    logo: "teads",
    implemented: false,
  },
  {
    id: "teamtailor",
    name: "Teamtailor",
    description: "Teamtailor is a recruitment software.",
    logo: "teamtailor",
    implemented: false,
  },
  {
    id: "teamwork",
    name: "Teamwork",
    description: "Teamwork is a project management platform.",
    logo: "teamwork",
    implemented: false,
  },
  {
    id: "tempo",
    name: "Tempo",
    description: "Tempo is a time management platform.",
    logo: "tempo",
    implemented: false,
  },
  {
    id: "testrail",
    name: "TestRail",
    description: "TestRail is a web-based test case management tool.",
    logo: "testrail",
    implemented: false,
  },
  {
    id: "textus",
    name: "TextUs",
    description:
      "TextUs is a cloud-based text messaging software for businesses.",
    logo: "textus",
    implemented: false,
  },
  {
    id: "the_movie_database",
    name: "The Movie Database",
    description: "The Movie Database (TMDB) is a movie and TV database.",
    logo: "the-movie-database",
    implemented: false,
  },
  {
    id: "the_trade_desk",
    name: "The Trade Desk",
    description:
      "The Trade Desk is an advertising service that provides users with a multifaceted approach to advertising automation.",
    logo: "the-trade-desk",
    implemented: false,
  },
  {
    id: "thinkific",
    name: "Thinkific",
    description:
      "Thinkific is a platform for creating and marketing online courses.",
    logo: "thinkific",
    implemented: false,
  },
  {
    id: "ticket_tailor",
    name: "Ticket Tailor",
    description: "Ticket Tailor is an event ticketing platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "tiktok_ads",
    name: "Tiktok Ads",
    description:
      "TikTok is a video-sharing social networking platform. TikTok Ads gives you information about the performance of your ads.",
    logo: "tiktok-organic",
    implemented: false,
  },
  {
    id: "tiktok_organic_app",
    name: "TikTok Organic",
    description: "TikTok is a short-form mobile video platform.",
    logo: "tiktok-organic",
    implemented: false,
  },
  {
    id: "tive",
    name: "Tive",
    description: "Tive is a supply chain visibility platform.",
    logo: null,
    implemented: false,
  },
  {
    id: "toggl_track",
    name: "Toggl Track",
    description:
      "Toggl Track is an online time tracking and management software.",
    logo: "toggl-track",
    implemented: false,
  },
  {
    id: "totango",
    name: "Totango",
    description: "Totango is a customer success platform.",
    logo: "totango",
    implemented: false,
  },
  {
    id: "tracksuit_source",
    name: "Tracksuit",
    description:
      "Tracksuit is a brand tracking dashboard for measuring the value of brand building activities.",
    logo: "tracksuit#tracksuit",
    implemented: false,
  },
  {
    id: "transcend",
    name: "Transcend",
    description: "Transcend is a data governance platform.",
    logo: "transcend",
    implemented: false,
  },
  {
    id: "trelica",
    name: "Trelica",
    description: "Trelica is a SaaS management platform.",
    logo: "trelica",
    implemented: false,
  },
  {
    id: "trello",
    name: "Trello",
    description: "Trello is a project management tool.",
    logo: "trello",
    implemented: false,
  },
  {
    id: "tremendous",
    name: "Tremendous",
    description:
      "Tremendous is a rewards platform for sending rewards, incentives, and payouts.",
    logo: null,
    implemented: false,
  },
  {
    id: "triple_whale",
    name: "Triple Whale",
    description: "Triple Whale is a marketing platform.",
    logo: "triple-whale",
    implemented: true,
  },
  {
    id: "trisolute",
    name: "Trisolute",
    description: "Trisolute is a news dashboard.",
    logo: "trisolute",
    implemented: false,
  },
  {
    id: "trustpilot",
    name: "Trustpilot",
    description:
      "Trustpilot is an online review community that connects businesses and consumers through genuine feedback from customers about their buying and service experiences.",
    logo: null,
    implemented: false,
  },
  {
    id: "twilio",
    name: "Twilio",
    description:
      "Twilio is a cloud communications platform that allows users to programmatically make and receive phone calls, send and receive text messages.",
    logo: null,
    implemented: false,
  },
  {
    id: "twitter",
    name: "Twitter Organic",
    description:
      "Twitter is a microblogging and social networking service. Fivetran's Twitter Organic connector provides a history of the settings and a detailed report of your organic tweet data.",
    logo: "twitter",
    implemented: false,
  },
  {
    id: "twitter_ads",
    name: "Twitter Ads",
    description:
      "Twitter Ads provides a history of the settings and detailed reports of your Twitter Ad Accounts, Campaigns, Line Items, Tweets, and Media Creatives.",
    logo: "twitter-ads",
    implemented: false,
  },
  {
    id: "tymeshift",
    name: "Tymeshift",
    description:
      "Tymeshift is a workforce management solution for Zendesk Support.",
    logo: "tymeshift",
    implemented: false,
  },
  {
    id: "typeform",
    name: "Typeform",
    description: "Typeform is an online survey management solution.",
    logo: "typeform",
    implemented: false,
  },
  {
    id: "udemy_business",
    name: "Udemy Business",
    description: "Udemy Business is an online learning platform.",
    logo: "udemy-business",
    implemented: false,
  },
  {
    id: "ukg_pro",
    name: "UKG Pro",
    description: "UKG Pro is a workforce management software solution.",
    logo: "ukg-pro-workforce-management",
    implemented: false,
  },
  {
    id: "ukg_pro_workforce_management",
    name: "UKG Pro Workforce Management",
    description:
      "UKG Pro Workforce Management is a cloud-native analytics platform",
    logo: "ukg-pro-workforce-management",
    implemented: false,
  },
  {
    id: "unicommerce",
    name: "Unicommerce",
    description:
      "Unicommerce provides e-commerce solutions to automate the supply chain of businesses.",
    logo: "unicommerce",
    implemented: false,
  },
  {
    id: "upland",
    name: "Upland Software",
    description: "Upland Software is a SMS marketing software.",
    logo: "upland",
    implemented: false,
  },
  {
    id: "uppromote",
    name: "UpPromote",
    description: "UpPromote is an affiliate and referral marketing platform.",
    logo: "uppromote",
    implemented: false,
  },
  {
    id: "uptempo",
    name: "Uptempo",
    description:
      "Uptempo is a Marketing Performance Management (MPM) platform.",
    logo: "uptempo",
    implemented: false,
  },
  {
    id: "uservoice",
    name: "Uservoice",
    description:
      "UserVoice is product feedback management software that enables businesses gather and analyze customer feedback and prioritize feature requests to drive strategic product decisions.",
    logo: "uservoice",
    implemented: false,
  },
  {
    id: "validio",
    name: "Validio",
    description: "Validio is a data quality and observability platform.",
    logo: "validio",
    implemented: false,
  },
  {
    id: "veeqo",
    name: "Veeqo",
    description: "Veeqo is an online inventory and order management platform.",
    logo: "veeqo",
    implemented: false,
  },
  {
    id: "veeva_vault",
    name: "Veeva Vault",
    description:
      "Veeva Vault is a cloud-based enterprise content management (ECM) platform designed specifically for the life sciences industry.",
    logo: "veeva-vault",
    implemented: false,
  },
  {
    id: "venminder",
    name: "Venminder",
    description: "Venminder is a risk management platform.",
    logo: "venminder",
    implemented: false,
  },
  {
    id: "vimeo",
    name: "Vimeo",
    description: "Vimeo is a video-sharing platform.",
    logo: "vimeo",
    implemented: false,
  },
  {
    id: "visit_by_ges",
    name: "Visit by GES",
    description: "Visit by GES is an event management platform.",
    logo: "visit-by-ges",
    implemented: false,
  },
  {
    id: "visma",
    name: "Visma",
    description:
      "Visma is a cloud accounting, payroll, and administration software.",
    logo: "visma",
    implemented: false,
  },
  {
    id: "vitally",
    name: "Vitally",
    description: "Vitally is a customer success platform.",
    logo: "vitally",
    implemented: false,
  },
  {
    id: "vonage_contact_center",
    name: "Vonage Contact Center",
    description: "Vonage is a cloud communications provider.",
    logo: "vonage-contact-center",
    implemented: false,
  },
  {
    id: "vts",
    name: "VTS",
    description: "VTS is a leasing and asset management platform.",
    logo: "vts",
    implemented: false,
  },
  {
    id: "vwo",
    name: "VWO",
    description: "VWO is a web testing and conversion optimization platform.",
    logo: "vwo",
    implemented: false,
  },
  {
    id: "walmart_dsp",
    name: "Walmart DSP",
    description:
      "Walmart DSP is an advertising service that provides users with a multifaceted approach to advertising automation.",
    logo: null,
    implemented: false,
  },
  {
    id: "walmart_marketplace",
    name: "Walmart Marketplace",
    description:
      "Walmart Marketplace is a platform for sellers to list and sell their products on Walmart's ecommerce website.",
    logo: "walmart-marketplace",
    implemented: false,
  },
  {
    id: "web_scraper",
    name: "Web Scraper",
    description: "Web Scraper is a web data extraction tool.",
    logo: "web-scraper",
    implemented: false,
  },
  {
    id: "webconnex",
    name: "Webconnex",
    description:
      "Webconnex develops fundraising, ticketing, and event management software products.",
    logo: "webconnex",
    implemented: false,
  },
  {
    id: "webhooks",
    name: "Webhooks",
    description:
      "A webhook is a user-defined HTTP callback triggered by an event on your website or in your application. A webhook allows you to send real-time HTTP notifications from one application to another whenever an event you specify occurs. A webhook URL receives JSON objects as HTTP POST requests.",
    logo: "webhooks",
    implemented: false,
  },
  {
    id: "when_i_work",
    name: "When I Work",
    description: "When I Work is a cloud-based employee scheduling solution.",
    logo: "when-i-work",
    implemented: false,
  },
  {
    id: "wherefour",
    name: "Wherefour",
    description:
      "Wherefour is a cloud-based software for traceability and inventory management.",
    logo: "wherefour",
    implemented: false,
  },
  {
    id: "wicked_reports",
    name: "Wicked Reports",
    description:
      "Wicked Reports is a multi-touch marketing attribution software that delivers critical data insights for your paid marketing activities.",
    logo: null,
    implemented: false,
  },
  {
    id: "woocommerce",
    name: "WooCommerce",
    description:
      "WooCommerce is an open-source e-commerce plugin for WordPress.",
    logo: "woocommerce",
    implemented: false,
  },
  {
    id: "wordpress",
    name: "WordPress",
    description: "WordPress is an open-source website creation platform.",
    logo: "wordpress",
    implemented: false,
  },
  {
    id: "workable",
    name: "Workable",
    description: "Workable is the world's leading hiring platform.",
    logo: "workable",
    implemented: false,
  },
  {
    id: "workday",
    name: "Workday RaaS",
    description:
      "Workday RaaS is a cloud ERP system for finance, HR, and planning.",
    logo: "workday-hcm",
    implemented: false,
  },
  {
    id: "workday_adaptive",
    name: "Workday Adaptive Planning",
    description:
      "Workday Adaptive Planning provides budgeting, forecasting, reporting, and analytics for businesses of all sizes.",
    logo: "workday-adaptive-planning#workdayadaptiveplanning",
    implemented: false,
  },
  {
    id: "workday_financial_management",
    name: "Workday Financial Management",
    description:
      "Workday Financial Management is a cloud-based financial management platform that brings Accounts, Accounting, Business Plans, Financial Reporting, Tax, Financial Organizations and related functions together in one system.",
    logo: "workday-financial-management",
    implemented: false,
  },
  {
    id: "workday_hcm",
    name: "Workday HCM",
    description:
      "Workday HCM is a cloud-based human capital management platform that brings HR, recruiting, staffing, benefits administration, compensation management and related functions together in one system.",
    logo: "workday-hcm",
    implemented: false,
  },
  {
    id: "workday_strategic_sourcing",
    name: "Workday Strategic Sourcing",
    description:
      "Workday Strategic Sourcing is a supply chain management platform.",
    logo: "workday-strategic-sourcing",
    implemented: false,
  },
  {
    id: "workleap_officevibe",
    name: "Workleap Officevibe",
    description: "Workleap Officevibe is an employee engagement platform.",
    logo: "workleap-officevibe",
    implemented: false,
  },
  {
    id: "workramp",
    name: "WorkRamp",
    description: "WorkRamp is a learning and enablement platform.",
    logo: "workramp",
    implemented: false,
  },
  {
    id: "wrike",
    name: "Wrike",
    description: "Wrike is a cloud-based work management platform.",
    logo: "wrike",
    implemented: false,
  },
  {
    id: "writesonic",
    name: "Writesonic",
    description:
      "Writesonic is an AI platform for creators and marketers to generate, optimize, and publish brand-ready content at scale.",
    logo: "writesonic",
    implemented: false,
  },
  {
    id: "xactly",
    name: "Xactly",
    description: "Xactly is a revenue platform.",
    logo: "xactly",
    implemented: false,
  },
  {
    id: "xandr",
    name: "Xandr",
    description: "Xandr is an advertising and analytics platform.",
    logo: "xandr",
    implemented: false,
  },
  {
    id: "xero",
    name: "Xero",
    description:
      "Xero provides cloud accounting services and includes a cashbook, automated daily bank feeds, invoicing, debtors, creditors, sales tax and reporting.",
    logo: "xero",
    implemented: false,
  },
  {
    id: "xray",
    name: "Xray",
    description: "Xray is a test management tool.",
    logo: "xray",
    implemented: false,
  },
  {
    id: "xsolla",
    name: "Xsolla",
    description: "Xsolla is a video game business platform.",
    logo: "xsolla",
    implemented: false,
  },
  {
    id: "yahoo_display_ads_on_yahoo_japan",
    name: "Yahoo Display Ads on Yahoo Japan",
    description:
      "Yahoo Display Ads on Yahoo Japan is an online advertising solution for displaying targeted ads across Yahoo Japan's websites.",
    logo: "yahoo",
    implemented: false,
  },
  {
    id: "yahoo_dsp",
    name: "Yahoo DSP",
    description:
      "Yahoo DSP is a management platform that gathers marketing data.",
    logo: "yahoo-dsp",
    implemented: false,
  },
  {
    id: "yahoo_search_ads_yahoo_japan",
    name: "Yahoo Search Ads on Yahoo Japan",
    description:
      "Yahoo Search Ads on Yahoo Japan enables businesses to display targeted ads on the search results pages of Yahoo Japan.",
    logo: "yahoo",
    implemented: false,
  },
  {
    id: "yext",
    name: "Yext",
    description:
      "Yext is a digital presence platform for multi-location brands.",
    logo: "yext",
    implemented: false,
  },
  {
    id: "yotpo",
    name: "Yotpo",
    description: "Yotpo is an ecommerce marketing platform.",
    logo: "yotpo",
    implemented: false,
  },
  {
    id: "yougov_brandindex",
    name: "YouGov BrandIndex",
    description: "YouGov BrandIndex is a market research tool.",
    logo: "yougov-brandindex",
    implemented: false,
  },
  {
    id: "yougov_sport",
    name: "YouGov Sport",
    description:
      "YouGov Sport is a sports, sponsorship, and entertainment research company.",
    logo: "yougov-sport",
    implemented: false,
  },
  {
    id: "younium",
    name: "Younium",
    description:
      "Younium is a subscription management and billing platform for B2B companies.",
    logo: "younium",
    implemented: false,
  },
  {
    id: "youtube_analytics",
    name: "YouTube Analytics",
    description:
      "YouTube Analytics Bulk Reports contain a comprehensive set of YouTube Analytics data for a channel or content owner.",
    logo: "youtube-analytics",
    implemented: false,
  },
  {
    id: "zendesk",
    name: "Zendesk Support",
    description: "Zendesk Support is a cloud-based customer support platform.",
    logo: "zendesk",
    implemented: false,
  },
  {
    id: "zendesk_chat",
    name: "Zendesk Chat",
    description:
      "Zendesk Chat creates customer engagement for online businesses with its live chat software.",
    logo: "zendesk-chat",
    implemented: false,
  },
  {
    id: "zendesk_sell",
    name: "Zendesk Sell",
    description:
      "Zendesk Sell is a sales CRM solution and sales force automation tool.",
    logo: "zendesk",
    implemented: false,
  },
  {
    id: "zendesk_sunshine",
    name: "Zendesk Sunshine",
    description:
      "Zendesk Sunshine is an open and flexible CRM platform. It lets you seamlessly connect and understand all your customer data. It is fast, powerful and gives you the freedom to build whatever you want.",
    logo: "zendesk-sunshine",
    implemented: false,
  },
  {
    id: "zendesk_workforce_management",
    name: "Zendesk Workforce Management",
    description:
      "Zendesk Workforce Management is a platform for managing staffing, scheduling, and performance tracking.",
    logo: "zendesk",
    implemented: false,
  },
  {
    id: "zenefits",
    name: "Zenefits",
    description:
      "Zenefits is a cloud-based human resources and payroll management platform.",
    logo: "zenefits",
    implemented: false,
  },
  {
    id: "zingtree",
    name: "Zingtree",
    description: "Zingtree is a no-code interactive decision tree platform.",
    logo: "zingtree",
    implemented: false,
  },
  {
    id: "zip",
    name: "Zip",
    description: "Zip is an intake and procurement orchestration platform.",
    logo: "zip",
    implemented: false,
  },
  {
    id: "zoho_books",
    name: "Zoho Books",
    description: "Zoho Books is an accounting software.",
    logo: "zoho-books",
    implemented: false,
  },
  {
    id: "zoho_campaigns",
    name: "Zoho Campaigns",
    description: "Zoho Campaigns is an email marketing automation software.",
    logo: "zoho-campaigns",
    implemented: false,
  },
  {
    id: "zoho_crm",
    name: "Zoho CRM",
    description:
      "Zoho CRM is an online CRM platform that manages your sales, marketing, and customer support activities.",
    logo: "zoho-desk",
    implemented: false,
  },
  {
    id: "zoho_desk",
    name: "Zoho Desk",
    description: "Zoho Desk is a customer service platform.",
    logo: "zoho-desk",
    implemented: false,
  },
  {
    id: "zoho_inventory",
    name: "Zoho Inventory",
    description:
      "Zoho Inventory is a cloud-based inventory management platform.",
    logo: "zoho-inventory",
    implemented: false,
  },
  {
    id: "zoho_recruit",
    name: "Zoho Recruit",
    description: "Zoho Recruit is a cloud-based recruitment platform.",
    logo: "zoho-recruit",
    implemented: false,
  },
  {
    id: "zonka_feedback",
    name: "Zonka Feedback",
    description: "Zonka Feedback is a customer survey and feedback platform.",
    logo: "zonka-feedback",
    implemented: false,
  },
  {
    id: "zoom",
    name: "Zoom",
    description: "Zoom is a video conferencing platform.",
    logo: "zoom",
    implemented: false,
  },
  {
    id: "zuora",
    name: "Zuora",
    description:
      "Zuora is a SaaS application designed to automate billing, commerce and finance operations. It helps companies build and run subscription businesses.",
    logo: "zuora",
    implemented: false,
  },
  {
    id: "zuora_sandbox",
    name: "Zuora Sandbox",
    description:
      "Zuora is a SaaS application designed to automate billing, commerce and finance operations. It helps companies build and run subscription businesses.",
    logo: "zuora",
    implemented: false,
  },
];

/** O(1) lookup of connector metadata by id. */
export const FIVETRAN_CONNECTORS_MAP = new Map(
  FIVETRAN_CONNECTORS.map((c) => [c.id, c]),
);
